// src/pages/VisionAssistSession.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// ✅ Vision chat
import VisionChatInterface from "../components/VisionChatInterface";

// ✅ Dedicated screen share + capture component (B/C aware via prop)
import VisionAssistScreenShare from "../components/VisionAssistScreenShare";

// ✅ Clock + Pause/Resume/End (MUST NOT reference assist mode)
import VisionAssistClockControls from "../components/VisionAssistClockControls";

// ✅ Use the established Vision Assist API contract for historical frame hydration.
import { getVisionFrameUrl as getVisionFrameUrlApi } from "../utils/visionAssistApi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const baseUrl = HOST ? `${HOST}` : "";

// -----------------------------------------------------------------------------
// VISION ASSIST SESSION LIFECYCLE — CURRENT PRODUCT POLICY
// -----------------------------------------------------------------------------
// Vision Assist sessions are persistent conversations. The visible clock remains
// authoritative/read-only, but the CLIENT must not auto-finalize, auto-stop,
// auto-redirect, or warn/stop merely because of inactivity, pause duration,
// browser navigation, page hide, or the local clock reaching 00:00.
//
// The remaining disabled timeout/auto-stop flags keep terminal client-side
// behavior unreachable; persistent-session navigation itself is canonical.
const VISION_AUTO_FINALIZE_ENABLED = false;
const VISION_INACTIVITY_AUTO_END_ENABLED = false;
const VISION_BROWSER_CLOSE_END_ENABLED = false;
const VISION_TIMEOUT_NOTICES_ENABLED = false;

// ✅ Analyze route (session-based; matches backend + API helper conventions)
function buildAnalyzePath(sessionId) {
  const sid = (sessionId || "").trim();
  if (!sid) return "";
  return `/vision/session/${encodeURIComponent(sid)}/analyze`;
}

// ✅ Dedicated frame upload route (NO /attachment/upload anywhere)
function buildFrameUploadPath(sessionId) {
  const sid = (sessionId || "").trim();
  if (!sid) return "";
  return `/vision/session/${encodeURIComponent(sid)}/frame/upload`;
}

// ✅ Filmstrip frames list endpoint (thumbnail strip)
function buildFramesListPath(sessionId) {
  const sid = (sessionId || "").trim();
  if (!sid) return "";
  return `/vision/session/${encodeURIComponent(sid)}/frames/list`;
}

// ----- small helpers (kept inside this file so you don't have to edit api helper yet) -----
async function getIdToken() {
  try {
    const user = getAuth()?.currentUser;
    if (!user) return "";
    return await user.getIdToken();
  } catch {
    return "";
  }
}

async function authedFetch(path, opts = {}) {
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated.");

  const headers = { ...(opts.headers || {}) };
  headers["Authorization"] = `Bearer ${token}`;
  if (!("Content-Type" in headers) && opts.method && opts.method !== "GET") {
    headers["Content-Type"] = "application/json";
  }

  const url = baseUrl ? `${baseUrl}${path}` : path;

  const res = await fetch(url, { ...opts, headers });
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }

  if (!res.ok || data?.success === false || data?.ok === false) {
    const reason =
      data?.error ||
      data?.message ||
      data?.reason ||
      res.statusText ||
      `HTTP ${res.status}`;
    const err = new Error(typeof reason === "string" ? reason : "Request failed");
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}

// ✅ Auth fetch for FormData (no JSON Content-Type)
async function authedFetchForm(path, formData, opts = {}) {
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated.");

  const headers = { ...(opts.headers || {}) };
  headers["Authorization"] = `Bearer ${token}`;

  const url = baseUrl ? `${baseUrl}${path}` : path;

  const res = await fetch(url, {
    method: "POST",
    ...opts,
    headers,
    body: formData,
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }

  if (!res.ok || data?.success === false || data?.ok === false) {
    const reason =
      data?.error ||
      data?.message ||
      data?.reason ||
      res.statusText ||
      `HTTP ${res.status}`;
    const err = new Error(typeof reason === "string" ? reason : "Request failed");
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function safeJsonParse(s) {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

// ✅ lightweight client_message_id generator
function uuidLike() {
  try {
    if (typeof crypto !== "undefined" && crypto?.randomUUID) return crypto.randomUUID();
  } catch {
    // ignore
  }
  return `cm_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// Normalize possible payload shapes (launcher camelCase OR older snake_case)
function normalizeLaunchPayload(raw, fallbackSessionId) {
  if (!raw || typeof raw !== "object") return null;

  const sid = raw.session_id || raw.sessionId || fallbackSessionId || "";
  const formType = raw.formType || raw.form_type || raw.form || "";
  const siteHost = raw.siteHost || raw.site_host || raw.site || "";
  const goal = raw.goal || "";
  const viewMode = raw.viewMode || raw.view_mode || "split";

  return {
    ...raw,
    session_id: sid,
    formType,
    siteHost,
    goal,
    viewMode,
  };
}

function readLaunchPayload(sessionId) {
  if (!sessionId) return null;

  try {
    const p = safeJsonParse(sessionStorage.getItem(`va_launch_payload:${sessionId}`));
    const norm = normalizeLaunchPayload(p, sessionId);
    if (norm?.session_id === sessionId) return norm;
  } catch {}

  try {
    const latest = safeJsonParse(sessionStorage.getItem("va_launch_payload_latest"));
    const norm = normalizeLaunchPayload(latest, sessionId);
    if (norm?.session_id === sessionId) return norm;
  } catch {}

  try {
    const ctx = safeJsonParse(sessionStorage.getItem(`va_session_ctx:${sessionId}`));
    const norm = normalizeLaunchPayload(ctx, sessionId);
    if (norm?.session_id === sessionId) return norm;
  } catch {}

  return null;
}

// ✅ Centered confirm modal (replaces ugly window.confirm)
function ConfirmModal({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true">
      <div style={styles.modalCard}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalMsg}>{message}</div>
        <div style={styles.modalActions}>
          <button type="button" style={{ ...styles.btn, ...styles.btnGhost }} onClick={onCancel}>
            {cancelText}
          </button>
          <button type="button" style={{ ...styles.btn, ...styles.btnDanger }} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ✅ Centered 2-action modal (inactivity/pause + ended modal)
function TwoActionModal({
  open,
  title,
  message,
  leftText,
  rightText,
  leftVariant = "ghost",
  rightVariant = "danger",
  extraLine = "",
  onLeft,
  onRight,
  leftDisabled = false,
  rightDisabled = false,
}) {
  if (!open) return null;

  const leftStyle =
    leftVariant === "danger"
      ? { ...styles.btn, ...styles.btnDanger }
      : leftVariant === "ghost"
      ? { ...styles.btn, ...styles.btnGhost }
      : { ...styles.btn };

  const rightStyle =
    rightVariant === "danger"
      ? { ...styles.btn, ...styles.btnDanger }
      : rightVariant === "ghost"
      ? { ...styles.btn, ...styles.btnGhost }
      : { ...styles.btn };

  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true">
      <div style={styles.modalCard}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalMsg}>{message}</div>
        {extraLine ? (
          <div style={{ ...styles.modalMsg, marginTop: 8, color: "#6b7280" }}>{extraLine}</div>
        ) : null}
        <div style={styles.modalActions}>
          <button type="button" style={leftStyle} onClick={onLeft} disabled={leftDisabled}>
            {leftText}
          </button>
          <button type="button" style={rightStyle} onClick={onRight} disabled={rightDisabled}>
            {rightText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ✅ Centered 1-action modal (low time)
function OneActionModal({ open, title, message, buttonText = "Got it", onClose, extraLine = "" }) {
  if (!open) return null;
  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true">
      <div style={styles.modalCard}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalMsg}>{message}</div>
        {extraLine ? (
          <div style={{ ...styles.modalMsg, marginTop: 8, color: "#6b7280" }}>{extraLine}</div>
        ) : null}
        <div style={styles.modalActions}>
          <button type="button" style={{ ...styles.btn }} onClick={onClose}>
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VisionAssistSession() {
  const navigate = useNavigate();
  const params = useParams();
  const q = useQuery();
  const location = useLocation();

  const sessionId = (params.sessionId || "").trim() || (q.get("session_id") || "").trim();

  // ✅ query-driven view (sidebar can open chat-only)
  const viewParam = String(q.get("view") || "").trim().toLowerCase();
  const chatOnlyRequested = viewParam === "chat";

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [idTokenReady, setIdTokenReady] = useState(false);

  const [userCtx, setUserCtx] = useState(null);

  const [launchCtx, setLaunchCtx] = useState({
    fromLauncher: false,
    gateOk: false,
    payload: null,
  });

  // ✅ launcher meta “sent once” guard (so Page 1/2/3 payload is not spammed)
  const launchMetaSentRef = useRef(false);
  const [launchMetaSent, setLaunchMetaSent] = useState(false);

  const readLaunchMetaSent = useCallback(() => {
    if (!sessionId) return false;
    try {
      return sessionStorage.getItem(`va_launch_meta_sent:${sessionId}`) === "1";
    } catch {
      return false;
    }
  }, [sessionId]);

  const markLaunchMetaSent = useCallback(() => {
    if (!sessionId) return;
    try {
      sessionStorage.setItem(`va_launch_meta_sent:${sessionId}`, "1");
    } catch {}
    launchMetaSentRef.current = true;
    setLaunchMetaSent(true);
  }, [sessionId]);

  const getLauncherPayloadForMeta = useCallback(() => {
    const p = launchCtx?.payload && typeof launchCtx.payload === "object" ? launchCtx.payload : null;
    if (!p) return null;

    const step1 = p.step1 || p.page1 || p.launcher_page_1 || p.launch_page_1 || p.launchStep1 || null;
    const step2 = p.step2 || p.page2 || p.launcher_page_2 || p.launch_page_2 || p.launchStep2 || null;
    const step3 = p.step3 || p.page3 || p.launcher_page_3 || p.launch_page_3 || p.launchStep3 || null;

    return {
      session_id: p.session_id || sessionId || "",
      formType: p.formType || "",
      siteHost: p.siteHost || "",
      goal: p.goal || "",
      viewMode: p.viewMode || "",

      page1: step1 || undefined,
      page2: step2 || undefined,
      page3: step3 || undefined,

      payload: p,
    };
  }, [launchCtx, sessionId]);

  const buildMetaWithLauncherOnce = useCallback(
    (meta = {}) => {
      const m = meta && typeof meta === "object" ? meta : {};
      if (launchMetaSentRef.current) return m;

      const launcher = getLauncherPayloadForMeta();
      if (!launcher) return m;

      return {
        ...m,
        launcher,
        launcher_sent_once: true,
      };
    },
    [getLauncherPayloadForMeta]
  );

  useEffect(() => {
    const sent = readLaunchMetaSent();
    launchMetaSentRef.current = sent;
    setLaunchMetaSent(sent);
  }, [readLaunchMetaSent]);

  const [snapshot, setSnapshot] = useState(null);
  const snapshotRef = useRef(null);
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  // ✅ Assistance mode is PARENT-owned
  // ✅ UPDATED: Auto-assist is disabled UI-wise. Force Step-by-step ("B") always.
  const [assistMode, _setAssistMode] = useState("B"); // "B" only
  const assistModeRef = useRef("B");
  useEffect(() => {
    assistModeRef.current = assistMode;
  }, [assistMode]);

  // ✅ Coerce any legacy/persisted attempt to enable auto mode back to B
  useEffect(() => {
    _setAssistMode("B");
    assistModeRef.current = "B";
  }, [sessionId]);

  const setAssistMode = useCallback((next) => {
    // never allow auto-assist to be set via UI/state/query/localStorage/etc.
    const v = String(next || "").toUpperCase();
    if (v !== "B") {
      _setAssistMode("B");
      assistModeRef.current = "B";
      return;
    }
    _setAssistMode("B");
    assistModeRef.current = "B";
  }, []);

  const [shareError, setShareError] = useState(null);
  const [isSharing, setIsSharing] = useState(false);

  // ✅ Dedicated screen share component ref (owns the stream + capture loop)
  const screenShareRef = useRef(null);

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });

  const [activeTab, setActiveTab] = useState("preview"); // "preview" | "chat"

  // ✅ Split layout sizing (desktop only)
  const [splitPct, setSplitPct] = useState(50);
  const draggingRef = useRef(false);

  const [paneOrder, setPaneOrder] = useState({ left: "preview", right: "chat" });
  const [collapsed, setCollapsed] = useState({ preview: false, chat: false });
  const [expanded, setExpanded] = useState(null); // null | "preview" | "chat"

  // ✅ Derived session flags
  const status = (snapshot?.status || "unknown").toLowerCase();
  const isPaused = status === "paused";
  const isActive = status === "active";
  const isCompleted =
    status === "completed" ||
    status === "exhausted" ||
    status === "aborted" ||
    status === "closed" ||
    status === "ended" ||
    status === "stopped";

  const remainingSeconds = useMemo(() => {
    // support multiple shapes + tolerate string formats like "120s" or "00:02:00"
    const raw =
      snapshot?.remaining_seconds ??
      snapshot?.remainingSeconds ??
      snapshot?.remaining_time_seconds ??
      snapshot?.remainingTimeSeconds ??
      snapshot?.time_remaining_seconds ??
      snapshot?.timeRemainingSeconds ??
      snapshot?.timer?.remaining_seconds ??
      snapshot?.timers?.remaining_seconds ??
      snapshot?.remaining?.seconds ??
      null;

    if (raw == null) return null;

    // already a number
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;

    const s = String(raw).trim();

    // if it's HH:MM:SS (or MM:SS), convert to seconds
    if (s.includes(":")) {
      const parts = s.split(":").map((p) => Number(p));
      if (parts.every((x) => Number.isFinite(x))) {
        let secs = 0;
        for (const x of parts) secs = secs * 60 + x;
        return Number.isFinite(secs) ? secs : null;
      }
    }

    // otherwise extract the first numeric token (handles "120s", "1,200", etc.)
    const m = s.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    if (!m) return null;

    const n = Number(m[0]);
    return Number.isFinite(n) ? n : null;
  }, [snapshot]);

  // ✅ Frontend-enforced end (optimistic UI on manual stop / browser close best-effort)
  const [forceEnded, setForceEnded] = useState(false);

  // ✅ More robust "ended" detection (covers manual stop/hard close shapes too)
  const endedByFields = !!(
    snapshot?.ended_at ||
    snapshot?.endedAt ||
    snapshot?.closed_at ||
    snapshot?.closedAt ||
    snapshot?.stopped_at ||
    snapshot?.stoppedAt ||
    snapshot?.is_closed ||
    snapshot?.isClosed ||
    snapshot?.closed === true ||
    snapshot?.ended === true
  );

  // The visible clock may reach 00:00, but this page no longer treats the
  // client-side countdown as an instruction to finalize/close the session.
  // Only an authoritative terminal backend/session state (or the dormant manual
  // stop path, if re-enabled in the future) can mark the session ended.
  const sessionEnded =
    !!forceEnded ||
    !!isCompleted ||
    !!endedByFields;

  // Legacy final-minute restriction is preserved for future reuse, but disabled
  // with the old timeout/finalize UX. While authoritative Vision minutes remain,
  // the session stays usable; backend billing/session policy remains authoritative.
  const LOW_TIME_SEC = 60;
  const legacyLowTimeMode =
    !sessionEnded &&
    !isCompleted &&
    typeof remainingSeconds === "number" &&
    remainingSeconds > 0 &&
    remainingSeconds <= LOW_TIME_SEC;

  const lowTimeMode =
    VISION_TIMEOUT_NOTICES_ENABLED &&
    legacyLowTimeMode;

  // ✅ Capability flags (PARENT is authority)
  const canChatInput = !sessionEnded && !isPaused; // lowTime still allows chat input
  const canAnalyze = !sessionEnded && !isPaused && !lowTimeMode;
  const canUpload = !sessionEnded && !isPaused && !lowTimeMode;

  // ✅ "Chat-only view" is now explicit only.
  // A timer/backend lifecycle change must not automatically collapse the split
  // Vision Assist workspace into transcript/chat-only mode.
  const chatOnlyView = !!chatOnlyRequested;

  // ✅ Top/bottom collapsers
  const [topCollapsed, setTopCollapsed] = useState(false);
  const [bottomCollapsed, setBottomCollapsed] = useState(false);

  // ✅ Modal confirmation state (end session)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    onConfirm: null,
  });

  // ✅ END SESSION: parent must treat child's end request as final (no double-confirm)
  const endRequestedRef = useRef(false);

  const openConfirm = ({ title, message, confirmText, onConfirm }) => {
    if (endRequestedRef.current) return;
    setConfirmState({
      open: true,
      title,
      message,
      confirmText: confirmText || "Confirm",
      onConfirm: onConfirm || null,
    });
  };

  const closeConfirm = () => {
    setConfirmState((s) => ({ ...s, open: false, onConfirm: null }));
  };

  // ✅ analyzing state (centered "Analyze my screen" button)
  const [analyzeBusy, setAnalyzeBusy] = useState(false);

  // ✅ hard lock (no racing) — one analyze at a time across manual + auto
  const analyzeInFlightRef = useRef(false);

  // ✅ THIS is the busy state you asked for (passed as prop to children)
  const isBusy = !!analyzeBusy || status === "analyzing" || status === "thinking";

  // ✅ NEW: busy mode string (analyzing vs thinking) — parent passes this to children
  const busyMode = useMemo(() => {
    if (analyzeBusy) return "analyzing"; // local/instant UI state wins
    if (status === "analyzing") return "analyzing";
    if (status === "thinking") return "thinking";
    return "";
  }, [analyzeBusy, status]);

  // ✅ auto analyze throttle for Mode C (still kept, but mode is forced to B)
  const lastAutoAnalyzeAtRef = useRef(0);

  // ✅ latest uploaded frame identity
  const [latestFrame, setLatestFrame] = useState(null);
  const latestFrameRef = useRef(null);
  useEffect(() => {
    latestFrameRef.current = latestFrame;
  }, [latestFrame]);

  // ✅ Filmstrip state (persisted via backend list + active selection in sessionStorage)
  const [frames, setFrames] = useState([]);
  const framesRef = useRef([]);
  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  // ✅ Frame URL cache is MEMORY-ONLY.
  // Signed GCS URLs are temporary credentials and must never be restored from
  // sessionStorage after a refresh. Canonical frame_id remains the authority.
  const [frameUrlCache, setFrameUrlCache] = useState({});
  const frameUrlCacheRef = useRef({});
  useEffect(() => {
    frameUrlCacheRef.current = frameUrlCache || {};
  }, [frameUrlCache]);

  // Event-driven rehydration policy:
  // - one fresh hydration pass after page/session load
  // - immediate re-sign on actual <img> failure
  // - refresh only after the tab/window returns from a meaningful idle period
  // - no per-minute/per-second signed-URL polling
  const FRAME_URL_REHYDRATE_AFTER_MS = 5 * 60 * 1000;
  const FRAME_URL_INITIAL_HYDRATE_COUNT = 8;

  // Navigation prefetch stays intentionally tiny: when a historical frame is
  // selected, hydrate that frame plus its immediate previous/next neighbors.
  // This makes arrow navigation feel instant without signing the entire history.
  const FRAME_URL_NAV_PREFETCH_RADIUS = 1;

  const frameUrlHydratedAtRef = useRef({});
  const frameUrlHydrationInFlightRef = useRef(new Map());
  const lastFrameHydrationSweepAtRef = useRef(0);
  const pageHiddenAtRef = useRef(0);

  // Clear all ephemeral signed-URL state when the session changes. Also remove
  // the legacy persisted cache so an old expired URL can never win on refresh.
  useEffect(() => {
    setFrameUrlCache({});
    frameUrlCacheRef.current = {};
    frameUrlHydratedAtRef.current = {};
    frameUrlHydrationInFlightRef.current = new Map();
    // Seed the sweep clock so an immediate browser focus event does not create
    // a duplicate hydration burst while the initial page-load pass is running.
    lastFrameHydrationSweepAtRef.current = Date.now();
    pageHiddenAtRef.current = 0;

    if (!sessionId) return;
    try {
      sessionStorage.removeItem(`va_frame_url_cache:${sessionId}`);
    } catch {}
  }, [sessionId]);

  const [activeFrameId, setActiveFrameId] = useState(null); // null => LIVE when sharing, or "last frame" selection when not sharing

  // ✅ Frontend-only navigation request for VisionChatInterface.
  // Historical thumbnail clicks request a jump to that frame's LATEST
  // qualifying single-frame Lexa response. The chat component decides whether
  // a valid single-frame anchor exists; multi-frame responses are intentionally ignored there.
  const chatFrameAnchorSeqRef = useRef(0);
  const [chatFrameAnchorRequest, setChatFrameAnchorRequest] = useState(null);

  const requestChatFrameAnchor = useCallback((frameId) => {
    const fid = String(frameId || "").trim();
    if (!fid || fid === "__LIVE__") return;

    chatFrameAnchorSeqRef.current += 1;
    setChatFrameAnchorRequest({
      frameId: fid,
      requestId: chatFrameAnchorSeqRef.current,
    });
  }, []);

  useEffect(() => {
    chatFrameAnchorSeqRef.current = 0;
    setChatFrameAnchorRequest(null);
  }, [sessionId]);

  // ✅ Explicit historical-frame multi-select.
  // Normal mode remains single-frame selection/reanalysis. Multi-select is opt-in,
  // historical frames only, and capped at five canonical frame_ids.
  const MULTI_FRAME_MAX = 5;
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedFrameIds, setSelectedFrameIds] = useState([]);

  const clearMultiSelection = useCallback(() => {
    setSelectedFrameIds([]);
    setMultiSelectMode(false);
  }, []);

  const toggleMultiFrameSelection = useCallback((frameId) => {
    const fid = String(frameId || "").trim();
    if (!fid || fid === "__LIVE__") return;

    setSelectedFrameIds((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(fid)) return current.filter((x) => x !== fid);
      if (current.length >= MULTI_FRAME_MAX) return current;
      return [...current, fid];
    });
  }, []);

  useEffect(() => {
    setSelectedFrameIds([]);
    setMultiSelectMode(false);
  }, [sessionId]);

  const lastFramesFetchAtRef = useRef(0);
  const framesFetchInFlightRef = useRef(false);
  const framesFetchPromiseRef = useRef(null);

  // ✅ restore last selected frame (survives refresh)
  useEffect(() => {
    if (!sessionId) return;
    try {
      const stored = String(sessionStorage.getItem(`va_active_frame:${sessionId}`) || "").trim();
      setActiveFrameId(stored || null);
    } catch {
      setActiveFrameId(null);
    }
  }, [sessionId]);

  // ✅ persist selection (survives refresh)
  useEffect(() => {
    if (!sessionId) return;
    try {
      const v = String(activeFrameId || "").trim();
      if (v) sessionStorage.setItem(`va_active_frame:${sessionId}`, v);
      else sessionStorage.removeItem(`va_active_frame:${sessionId}`);
    } catch {}
  }, [sessionId, activeFrameId]);

  // ✅ Normalize frame identity returned by capture/upload
  const normalizeFrameIdentity = useCallback((raw) => {
    const d = raw && typeof raw === "object" ? raw : {};
    const snap =
      snapshotRef.current && typeof snapshotRef.current === "object"
        ? snapshotRef.current
        : {};

    const image_url =
      String(
        d.image_url ||
          d.imageUrl ||
          d.url ||
          d.file_url ||
          d.fileUrl ||
          d.signed_url ||
          d.signedUrl ||
          d.download_url ||
          d.downloadUrl ||
          d.token_url ||
          d.tokenUrl ||
          d.fallback_token_url ||
          ""
      ).trim() || "";

    const image_proxy_url =
      String(
        d.image_proxy_url ||
          d.imageProxyUrl ||
          d.proxy_url ||
          d.proxyUrl ||
          ""
      ).trim() || "";

    const thumb_url =
      String(
        d.thumb_url ||
          d.thumbUrl ||
          d.thumbnail_url ||
          d.thumbnailUrl ||
          (d.meta && (d.meta.thumb_url || d.meta.thumbnail_url || d.meta.thumbnailUrl)) ||
          ""
      ).trim() || "";

    const local_url =
      String(
        d.local_url ||
          d.localUrl ||
          d.preview_url ||
          d.previewUrl ||
          d.data_url ||
          d.dataUrl ||
          d.preview_data_url ||
          d.previewDataUrl ||
          ""
      ).trim() || "";

    const local_thumb_url =
      String(
        d.local_thumb_url ||
          d.localThumbUrl ||
          d.preview_thumb_url ||
          d.previewThumbUrl ||
          d.thumb_data_url ||
          d.thumbDataUrl ||
          ""
      ).trim() || "";

    const frame_id = String(d.frame_id || d.frameId || d.id || d.frame || "").trim() || "";

    const session_doc_path =
      String(
        d.session_doc_path ||
          d.sessionDocPath ||
          snap.session_doc_path ||
          snap.sessionDocPath ||
          ""
      ).trim() || "";

    const bucket = String(d.bucket || d.frame_bucket || "").trim() || "";
    const object_name =
      String(
        d.object_name ||
          d.objectName ||
          d.frame_object_name ||
          d.gcs_object ||
          d.object ||
          ""
      ).trim() || "";

    const access_kind = String(d.access_kind || d.accessKind || "").trim() || "";

    const meta = d.meta && typeof d.meta === "object" ? d.meta : {};

    return {
      image_url,
      image_proxy_url,
      thumb_url,
      local_url,
      local_thumb_url,
      frame_id,
      session_doc_path,
      bucket,
      object_name,
      access_kind,
      meta,
      raw: d,
    };
  }, []);

  // ✅ Normalize filmstrip list items
  const normalizeFilmstripItem = useCallback((raw) => {
    const r = raw && typeof raw === "object" ? raw : {};
    const frame_id = String(r.frame_id || r.frameId || r.id || "").trim();
    if (!frame_id) return null;

    const meta = r.meta && typeof r.meta === "object" ? r.meta : {};

    const thumb_url = String(
      r.thumb_url ||
        r.thumbnail_url ||
        r.thumbnailUrl ||
        r.thumbUrl ||
        r.thumb ||
        meta.thumb_url ||
        meta.thumbnail_url ||
        meta.thumbnailUrl ||
        ""
    ).trim();

    const image_url = String(
      r.image_url ||
        r.imageUrl ||
        r.url ||
        r.signed_url ||
        r.signedUrl ||
        r.download_url ||
        r.downloadUrl ||
        r.token_url ||
        r.tokenUrl ||
        meta.image_url ||
        meta.imageUrl ||
        meta.signed_url ||
        meta.signedUrl ||
        ""
    ).trim();

    const image_proxy_url = String(
      r.image_proxy_url ||
        r.imageProxyUrl ||
        r.proxy_url ||
        r.proxyUrl ||
        meta.image_proxy_url ||
        meta.imageProxyUrl ||
        meta.proxy_url ||
        meta.proxyUrl ||
        ""
    ).trim();

    const at_ms_raw =
      r.captured_at_ms ||
      r.capturedAtMs ||
      r.created_at_ms ||
      r.createdAtMs ||
      r.ts_ms ||
      r.tsMs ||
      r.at_ms ||
      r.atMs ||
      meta.captured_at_ms ||
      meta.created_at_ms ||
      null;

    const at_ms = typeof at_ms_raw === "number" ? at_ms_raw : Number(at_ms_raw || 0) || 0;

    return {
      kind: "frame",
      frame_id,
      thumb_url,
      image_url,
      image_proxy_url,
      local_url: "",
      local_thumb_url: "",
      at_ms,
      raw: r,
    };
  }, []);

  // ✅ Filmstrip filter: keep ONLY screen-share / parent-captured frames
// Goal: filter out manual uploads that came from attachments/doc upload.
const isParentCapturedFrame = useCallback((item) => {
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : {};
  const meta = raw?.meta && typeof raw.meta === "object" ? raw.meta : {};

  // Read common signals (normalize to lowercase strings)
  const trigger = String(meta.trigger || raw.trigger || "").trim().toLowerCase();
  const source = String(meta.source || raw.source || "").trim().toLowerCase();
  const origin = String(meta.origin || raw.origin || "").trim().toLowerCase();
  const kind = String(meta.kind || raw.kind || meta.type || raw.type || "").trim().toLowerCase();
  const accessKind = String(raw.access_kind || meta.access_kind || raw.accessKind || meta.accessKind || "")
    .trim()
    .toLowerCase();

  // ✅ Hard BLOCK (ONLY): attachment uploads (this is the *one* specific thing we filter out)
  // Backend plan: meta.source === "attachment_upload" for manual uploads from attachment controller.
  const isAttachmentUpload =
    source === "attachment_upload" ||
    accessKind === "attachment_upload" ||
    kind === "attachment_upload" ||
    trigger === "attachment_upload" ||
    origin === "attachment_upload";

  if (isAttachmentUpload) return false;

  // ✅ Everything else is allowed (do NOT add more filters here)
  return true;
}, []);


  // ✅ One fetch at a time, but callers can await the in-flight promise
  const fetchFramesList = useCallback(
    async ({ force = false, silent = true, allowEnded = false } = {}) => {
      if (!sessionId) return;
      if (sessionEnded && !allowEnded) return; // ✅ background refresh still stops after end; history may hydrate once
      const path = buildFramesListPath(sessionId);
      if (!path) return;

      if (framesFetchInFlightRef.current) {
        return framesFetchPromiseRef.current || undefined;
      }

      const now = Date.now();
      const last = lastFramesFetchAtRef.current || 0;
      if (!force && now - last < 1500) return;

      framesFetchInFlightRef.current = true;
      lastFramesFetchAtRef.current = now;

      const run = (async () => {
        try {
          const data = await authedFetch(path, { method: "GET" });

          const arr =
            (Array.isArray(data?.frames) && data.frames) ||
            (Array.isArray(data?.items) && data.items) ||
            (Array.isArray(data?.data) && data.data) ||
            (Array.isArray(data) && data) ||
            [];

          const normalized = [];
          for (const it of arr) {
            const n = normalizeFilmstripItem(it);
            if (n) normalized.push(n);
          }

          normalized.sort((a, b) => (a.at_ms || 0) - (b.at_ms || 0));

          const cache = frameUrlCacheRef.current || {};
          const merged = normalized.map((x) => {
            const c = cache[String(x.frame_id || "")] || null;
            if (!c) return x;
            return {
              ...x,
              // Memory cache contains freshly re-signed URLs and therefore wins
              // over URLs embedded in an older frames/list payload.
              image_url: String(c.image_url || "").trim() || String(x.image_url || "").trim() || "",
              image_proxy_url:
                String(c.image_proxy_url || "").trim() || String(x.image_proxy_url || "").trim() || "",
              thumb_url: String(c.thumb_url || "").trim() || String(x.thumb_url || "").trim() || "",
              local_url: String(c.local_url || "").trim() || String(x.local_url || "").trim() || "",
              local_thumb_url:
                String(c.local_thumb_url || "").trim() || String(x.local_thumb_url || "").trim() || "",
            };
          });
          // ✅ Apply filter: only keep parent/screen-share frames
          const mergedFiltered = merged.filter(isParentCapturedFrame);

          setFrameUrlCache((prev) => {
            const p = prev && typeof prev === "object" ? prev : {};
            let changed = false;
            const next = { ...p };
            for (const it of mergedFiltered) {
              const fid = String(it?.frame_id || "").trim();
              if (!fid) continue;
              const cur = (next[fid] && typeof next[fid] === "object") ? next[fid] : {};
              const nImage = String(it?.image_url || "").trim();
              const nProxy = String(it?.image_proxy_url || "").trim();
              const nThumb = String(it?.thumb_url || "").trim();
              const nLocal = String(it?.local_url || "").trim();
              const nLocalThumb = String(it?.local_thumb_url || "").trim();

              const updated = {
                ...cur,
                image_url: nImage || cur.image_url || "",
                image_proxy_url: nProxy || cur.image_proxy_url || "",
                thumb_url: nThumb || cur.thumb_url || "",
                local_url: nLocal || cur.local_url || "",
                local_thumb_url: nLocalThumb || cur.local_thumb_url || "",
              };

              if (
                updated.image_url !== cur.image_url ||
                updated.image_proxy_url !== cur.image_proxy_url ||
                updated.thumb_url !== cur.thumb_url ||
                updated.local_url !== cur.local_url ||
                updated.local_thumb_url !== cur.local_thumb_url
              ) {
                next[fid] = updated;
                changed = true;
              }
            }
            return changed ? next : p;
          });

          setFrames(mergedFiltered);


          setActiveFrameId((prev) => {
            if (isSharing) {
              if (!prev) return null;
              const exists = mergedFiltered.some((x) => x.frame_id === prev);
              return exists ? prev : null;
            }

            if (prev) {
              const exists = mergedFiltered.some((x) => x.frame_id === prev);
              return exists
                ? prev
                : mergedFiltered.length
                ? mergedFiltered[mergedFiltered.length - 1].frame_id
                : null;
            }

            return mergedFiltered.length
              ? mergedFiltered[mergedFiltered.length - 1].frame_id
              : null;
          });

        } catch (e) {
          if (!silent) {
            setNotice({ type: "error", msg: e?.message || "Failed to load frames list." });
          }
        }
      })();

      framesFetchPromiseRef.current = run;

      try {
        return await run;
      } finally {
        framesFetchPromiseRef.current = null;
        framesFetchInFlightRef.current = false;
      }
    },
    [sessionId, normalizeFilmstripItem, isParentCapturedFrame, isSharing, sessionEnded]

  );

  const upsertFrameIntoFilmstrip = useCallback(
    (frameIdentity) => {
      if (sessionEnded) return;
      const fid = String(frameIdentity?.frame_id || "").trim();
      if (!fid) return;

      const imgUrl = String(frameIdentity?.image_url || "").trim();
      const proxyUrl = String(frameIdentity?.image_proxy_url || "").trim();
      const thUrl = String(frameIdentity?.thumb_url || "").trim();
      const localUrl = String(frameIdentity?.local_url || "").trim();
      const localThumb = String(frameIdentity?.local_thumb_url || "").trim();

      setFrameUrlCache((prev) => {
        const p = prev && typeof prev === "object" ? prev : {};
        const cur = p[fid] && typeof p[fid] === "object" ? p[fid] : {};
        return {
          ...p,
          [fid]: {
            ...cur,
            image_url: imgUrl || cur.image_url || "",
            image_proxy_url: proxyUrl || cur.image_proxy_url || "",
            thumb_url: thUrl || cur.thumb_url || "",
            local_url: localUrl || cur.local_url || "",
            local_thumb_url: localThumb || cur.local_thumb_url || "",
            hydrated_at_ms: imgUrl || thUrl ? Date.now() : Number(cur.hydrated_at_ms || 0),
          },
        };
      });

      if (imgUrl || thUrl) {
        frameUrlHydratedAtRef.current = {
          ...(frameUrlHydratedAtRef.current || {}),
          [fid]: Date.now(),
        };
      }

      const item = {
        kind: "frame",
        frame_id: fid,
        local_thumb_url: localThumb || "",
        thumb_url: thUrl || "",
        local_url: localUrl || "",
        image_url: imgUrl || "",
        image_proxy_url: proxyUrl || "",
        at_ms: Date.now(),
        raw: frameIdentity?.raw || frameIdentity || {},
      };

      setFrames((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((x) => x.frame_id === fid);
        if (idx >= 0) {
          next[idx] = { ...next[idx], ...item };
          return next;
        }
        next.push(item);
        return next;
      });
    },
    [sessionEnded]
  );

 

  
  // ✅ NEW: local frame lookup helpers (used by chat to resolve frame_id -> URL reliably)
  const getFrameById = useCallback((frameId) => {
    const fid = String(frameId || "").trim();
    if (!fid) return null;
    const list = framesRef.current || [];
    return list.find((x) => String(x?.frame_id || "") === fid) || null;
  }, []);

  // ✅ Resolve canonical frame_id -> display URL through visionAssistApi.
  // The shared API helper owns the correct Main contract and returns both a
  // temporary signed image_url and a stable image_proxy_url fallback.
  const getVisionFrameUrl = useCallback(
    async (frameId, { prefer = "image", forceRefresh = false } = {}) => {
      const fid = String(frameId || "").trim();
      if (!sessionId || !fid) return "";

      const cache = frameUrlCacheRef.current || {};
      const cached = cache[fid] || null;
      const item = getFrameById(fid);

      const localImage =
        String(cached?.local_url || "").trim() ||
        String(item?.local_url || "").trim();
      const localThumb =
        String(cached?.local_thumb_url || "").trim() ||
        String(item?.local_thumb_url || "").trim();

      if (prefer === "thumb" && localThumb) return localThumb;
      if (prefer !== "thumb" && localImage) return localImage;

      const hydratedAt = Number(frameUrlHydratedAtRef.current?.[fid] || 0);
      const memoryImage =
        String(cached?.image_url || "").trim() ||
        String(cached?.image_proxy_url || "").trim();
      const memoryThumb =
        String(cached?.thumb_url || "").trim() ||
        memoryImage;
      const memoryUrl = prefer === "thumb" ? memoryThumb : memoryImage;

      if (
        !forceRefresh &&
        memoryUrl &&
        hydratedAt > 0 &&
        Date.now() - hydratedAt < FRAME_URL_REHYDRATE_AFTER_MS
      ) {
        return memoryUrl;
      }

      if (!forceRefresh && !hydratedAt) {
        const listImage =
          String(item?.image_url || "").trim() ||
          String(item?.image_proxy_url || "").trim();
        const listThumb = String(item?.thumb_url || "").trim() || listImage;
        const listUrl = prefer === "thumb" ? listThumb : listImage;
        if (listUrl) return listUrl;
      }

      const inflightKey = fid;
      const existing = frameUrlHydrationInFlightRef.current.get(inflightKey);
      if (existing) return existing;

      const request = (async () => {
        try {
          const out = await getVisionFrameUrlApi(sessionId, fid);
          const freshImage = String(out?.image_url || "").trim();
          const freshProxy = String(out?.image_proxy_url || "").trim();
          const finalUrl = freshImage || freshProxy;
          if (!finalUrl) return "";

          const hydratedAtMs = Date.now();
          frameUrlHydratedAtRef.current = {
            ...(frameUrlHydratedAtRef.current || {}),
            [fid]: hydratedAtMs,
          };

          setFrameUrlCache((prev) => {
            const p = prev && typeof prev === "object" ? prev : {};
            const cur = p[fid] && typeof p[fid] === "object" ? p[fid] : {};
            const next = {
              ...p,
              [fid]: {
                ...cur,
                image_url: freshImage || "",
                image_proxy_url: freshProxy || "",
                thumb_url:
                  String(cur.thumb_url || "").trim() ||
                  freshImage ||
                  freshProxy ||
                  "",
                hydrated_at_ms: hydratedAtMs,
              },
            };
            frameUrlCacheRef.current = next;
            return next;
          });

          return finalUrl;
        } catch {
          return "";
        }
      })();

      frameUrlHydrationInFlightRef.current.set(inflightKey, request);
      try {
        return await request;
      } finally {
        frameUrlHydrationInFlightRef.current.delete(inflightKey);
      }
    },
    [sessionId, getFrameById]
  );

  const resolveFrameUrl = useCallback(
    async (frameId, { prefer = "image", forceRefresh = false } = {}) => {
      const fid = String(frameId || "").trim();
      if (!fid) return "";

      const direct = await getVisionFrameUrl(fid, { prefer, forceRefresh }).catch(() => "");
      if (direct) return direct;

      // Only refresh the frame index after a true forced recovery miss. This is
      // event-driven, not a periodic polling loop.
      if (forceRefresh) {
        await fetchFramesList({ force: true, silent: true }).catch(() => {});
        return await getVisionFrameUrl(fid, { prefer, forceRefresh: true }).catch(() => "");
      }

      return "";
    },
    [getVisionFrameUrl, fetchFramesList]
  );

  // ✅ Initial page/session hydration: fresh-sign only the most recent frames.
  // This runs once per frame per page lifecycle, not on an interval. Older frames
  // hydrate lazily when selected or when their <img> actually fails.
  const hydratedSetRef = useRef(new Set());
  useEffect(() => {
    hydratedSetRef.current = new Set();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (sessionEnded) return;

    const list = Array.isArray(frames) ? frames : [];
    if (!list.length) return;

    const recent = list.slice(-FRAME_URL_INITIAL_HYDRATE_COUNT);
    let cancelled = false;

    (async () => {
      for (const it of recent) {
        if (cancelled) return;
        const fid = String(it?.frame_id || "").trim();
        if (!fid) continue;
        if (hydratedSetRef.current.has(fid)) continue;

        hydratedSetRef.current.add(fid);
        await getVisionFrameUrl(fid, { prefer: "thumb", forceRefresh: true }).catch(() => "");
      }

      if (!cancelled) lastFrameHydrationSweepAtRef.current = Date.now();
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, frames, getVisionFrameUrl, sessionEnded]);

  // ✅ Return-from-idle hydration. No timer/poller is created. We refresh only
  // when the user actually comes back after a meaningful idle period.
  useEffect(() => {
    if (!sessionId || sessionEnded) return undefined;

    let sweepInFlight = false;

    const refreshAfterIdle = async () => {
      if (sweepInFlight) return;

      const now = Date.now();
      const hiddenAt = Number(pageHiddenAtRef.current || 0);
      const lastSweep = Number(lastFrameHydrationSweepAtRef.current || 0);
      const awayMs = hiddenAt ? now - hiddenAt : now - lastSweep;

      if (awayMs < FRAME_URL_REHYDRATE_AFTER_MS) return;
      sweepInFlight = true;
      pageHiddenAtRef.current = 0;

      try {
        await fetchFramesList({ force: true, silent: true }).catch(() => {});

        const current = Array.isArray(framesRef.current) ? framesRef.current : [];
        const active = String(activeFrameId || "").trim();
        const ids = Array.from(
          new Set([
            ...(active && active !== "__LIVE__" ? [active] : []),
            ...current
              .slice(-FRAME_URL_INITIAL_HYDRATE_COUNT)
              .map((x) => String(x?.frame_id || "").trim())
              .filter(Boolean),
          ])
        );

        // Sequential requests avoid a burst of signer traffic.
        for (const fid of ids) {
          await getVisionFrameUrl(fid, {
            prefer: fid === active ? "image" : "thumb",
            forceRefresh: true,
          }).catch(() => "");
        }

        lastFrameHydrationSweepAtRef.current = Date.now();
      } finally {
        sweepInFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pageHiddenAtRef.current = Date.now();
        return;
      }
      refreshAfterIdle().catch(() => {});
    };

    const onFocus = () => {
      refreshAfterIdle().catch(() => {});
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [sessionId, sessionEnded, activeFrameId, fetchFramesList, getVisionFrameUrl]);

  // ---------- Responsive watcher ----------
  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth >= 1024;
      setIsDesktop(next);
      if (!next) {
        setExpanded(null);
        setCollapsed({ preview: false, chat: false });
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ---------- Auth watcher ----------
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsLoggedIn(false);
        setIdTokenReady(true);
        setUserCtx(null);
        return;
      }

      setIsLoggedIn(true);
      setIdTokenReady(true);

      setUserCtx({
        uid: user.uid || "",
        email: user.email || "",
        displayName: user.displayName || "",
        phoneNumber: user.phoneNumber || "",
      });
    });
    return () => unsub();
  }, []);

  // ---------- Persistent session entry context ----------
  // Vision sessions can be opened either immediately after creation or later
  // from Sidebar history. Browser/sessionStorage launcher gates are not access
  // control; the authenticated backend session lookup below is authoritative.
  useEffect(() => {
    if (!sessionId) return;

    const payload = readLaunchPayload(sessionId);
    const fromLauncher = !!location?.state?.va_from_launcher;

    setLaunchCtx({
      fromLauncher,
      gateOk: true,
      payload: payload || null,
    });
  }, [sessionId, location?.state?.va_from_launcher]);

  // ---------- Fetch session snapshot (authoritative session doc) ----------
  const fetchSnapshot = useCallback(async () => {
    if (!sessionId) return;
    if (sessionEnded) return; // ✅ stop reads after ended to prevent last_seen ticking

    const data = await authedFetch(`/vision/session/${encodeURIComponent(sessionId)}`, {
      method: "GET",
    });

    // ✅ unwrap common response shapes: {session:{...}} or {data:{...}} or direct doc
    const doc = (data?.session && typeof data.session === "object")
      ? data.session
      : (data?.data && typeof data.data === "object")
      ? data.data
      : data;

    setSnapshot(doc);

    try {
      sessionStorage.setItem(
        `va_session_ctx:${sessionId}`,
        JSON.stringify({
          at: Date.now(),
          session_id: sessionId,
          formType: doc?.form_type || doc?.formType || "",
          siteHost: doc?.site_host || doc?.siteHost || "",
          goal: doc?.goal || "",
          viewMode: doc?.view_mode || doc?.viewMode || "",
          worker_region: doc?.worker_region || doc?.workerRegion || "",
          session_doc_path: doc?.session_doc_path || doc?.sessionDocPath || "",
          messages_col_path: doc?.messages_col_path || doc?.messagesColPath || "",
        })
      );
    } catch {}

    try {
      const existing = safeJsonParse(sessionStorage.getItem(`va_launch_payload:${sessionId}`));
      if (!existing) {
        sessionStorage.setItem(
          `va_launch_payload:${sessionId}`,
          JSON.stringify({
            v: 1,
            session_id: sessionId,
            launched_at_ms: Date.now(),
            source: "session_snapshot",
            formType: doc?.form_type || "",
            siteHost: doc?.site_host || "",
            goal: doc?.goal || "",
            viewMode: doc?.view_mode || "split",
          })
        );
      }
    } catch {}

    setLaunchCtx((prev) => {
      if (prev?.payload) return prev;
      const p = readLaunchPayload(sessionId);
      return { ...prev, payload: p || null };
    });
  }, [sessionId, sessionEnded]);

  // Initial load
  useEffect(() => {
    if (!idTokenReady) return;

    if (!isLoggedIn) {
      navigate("/login", {
        state: {
          from: sessionId
            ? `/vision/session/${encodeURIComponent(sessionId)}`
            : "/vision-assist",
        },
      });
      return;
    }

    if (!sessionId) {
      setLoading(false);
      setNotice({ type: "error", msg: "Missing session_id in the URL." });
      return;
    }

    (async () => {
      try {
        setLoading(true);
        await fetchSnapshot();
        setNotice(null);
      } catch (e) {
        setNotice({
          type: "error",
          msg: e?.message || "Failed to load session.",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [idTokenReady, isLoggedIn, sessionId, navigate, fetchSnapshot]);

  // ✅ Always load filmstrip frames for this session after login/snapshot
  useEffect(() => {
    if (!sessionId) return;
    if (!idTokenReady || !isLoggedIn) return;
    if (sessionEnded) return;
    fetchFramesList({ force: true, silent: true, allowEnded: true }).catch(() => {});
  }, [sessionId, idTokenReady, isLoggedIn, fetchFramesList, sessionEnded]);

  // Poll authoritative snapshot every 5 seconds (stop when ended)
  useEffect(() => {
    if (!sessionId || !isLoggedIn) return;
    if (sessionEnded) return;

    const t = setInterval(() => {
      fetchSnapshot().catch(() => {});
    }, 5000);

    return () => clearInterval(t);
  }, [sessionId, isLoggedIn, fetchSnapshot, sessionEnded]);

  // Keepalive ping every 20 seconds (ONLY while active; do not ping when paused/ended/low-time)
  useEffect(() => {
    if (!sessionId || !isLoggedIn) return;
    if (!isActive) return;
    if (sessionEnded) return;

    const t = setInterval(() => {
      authedFetch(`/vision/session/${encodeURIComponent(sessionId)}/ping`, {
        method: "POST",
        body: JSON.stringify({}),
      }).catch(() => {});
    }, 120000);

    return () => clearInterval(t);
  }, [sessionId, isLoggedIn, isActive, sessionEnded]);

  // ---------- Controls (parent owns backend calls) ----------
  const [pauseBusy, setPauseBusy] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  const endInFlightRef = useRef(false);

  // =========================
  // ✅ Activity + Inactivity + Pause Monitoring (PARENT owns timers)
  // =========================
  const INACTIVE_AFTER_MS = 5 * 60 * 1000; // 5 min
  const PAUSE_AFTER_MS = 10 * 60 * 1000; // 10 min
  const GRACE_MS = 60 * 1000; // 60 sec

  const lastActivityAtRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const graceIntervalRef = useRef(null);
  const mouseMoveThrottleRef = useRef(0);

  const [attentionModal, setAttentionModal] = useState({
    open: false,
    kind: "",
    graceLeftSec: 0,
  });

  const clearAttentionTimers = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    if (graceIntervalRef.current) clearInterval(graceIntervalRef.current);

    inactivityTimerRef.current = null;
    pauseTimerRef.current = null;
    graceTimerRef.current = null;
    graceIntervalRef.current = null;
  }, []);

  const closeAttentionModal = useCallback(() => {
    setAttentionModal({ open: false, kind: "", graceLeftSec: 0 });
    if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    if (graceIntervalRef.current) clearInterval(graceIntervalRef.current);
    graceTimerRef.current = null;
    graceIntervalRef.current = null;
  }, []);

  const markActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now();

    // Legacy inactivity auto-finalize logic is intentionally dormant.
    if (!VISION_INACTIVITY_AUTO_END_ENABLED) return;

    if (!attentionModal.open && isActive && !sessionEnded && !isPaused) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        setAttentionModal({
          open: true,
          kind: "inactivity",
          graceLeftSec: Math.ceil(GRACE_MS / 1000),
        });
      }, INACTIVE_AFTER_MS);
    }
  }, [attentionModal.open, isActive, sessionEnded, isPaused, GRACE_MS, INACTIVE_AFTER_MS]);

  const stopSessionDirect = useCallback(
    async ({ reason = "auto_end" } = {}) => {
      // Session finalization is currently disabled product-wide on this page.
      // Keep the legacy implementation below dormant for future reuse.
      if (!VISION_AUTO_FINALIZE_ENABLED) return;
      if (!sessionId) return;
      if (endInFlightRef.current) return;
      if (endBusy) return;

      endRequestedRef.current = true;

      // ✅ optimistic end: enforce UI immediately
      setForceEnded(true);

      endInFlightRef.current = true;
      setEndBusy(true);

      closeConfirm();

      try {
        setNotice(null);

        try {
          await screenShareRef.current?.stopSharing?.("session_end");
        } catch {}
        setIsSharing(false);
        setAnalyzeBusy(false);
        analyzeInFlightRef.current = false;
        setActiveFrameId(null);

        await authedFetch(`/vision/session/${encodeURIComponent(sessionId)}/stop`, {
          method: "POST",
          body: JSON.stringify({ reason: String(reason || "auto_end") }),
        });

        closeAttentionModal();
        clearAttentionTimers();

        // ✅ do not fetchSnapshot here (it can tick last_seen); the UI is already ended
        setNotice({ type: "ok", msg: "Session ended." });
      } catch (e) {
        // if stop fails, revert forceEnded so user isn't stuck (optional)
        setForceEnded(false);
        endRequestedRef.current = false;
        setNotice({ type: "error", msg: e?.message || "Stop failed." });
      } finally {
        setEndBusy(false);
        endInFlightRef.current = false;
      }
    },
    [sessionId, endBusy, clearAttentionTimers, closeAttentionModal]
  );

  // ✅ NEW: Handle browser/app close -> end session immediately (best-effort).
  // Note: fetch may not complete; sendBeacon is best effort.
  useEffect(() => {
    // Do not warn, auto-stop, or finalize when the user navigates away,
    // refreshes, closes the tab, backgrounds the page, or closes the browser.
    if (!VISION_BROWSER_CLOSE_END_ENABLED) return undefined;
    if (!sessionId) return undefined;

    const sendEndBeacon = (reason) => {
      try {
        const url = baseUrl
          ? `${baseUrl}/vision/session/${encodeURIComponent(sessionId)}/stop`
          : `/vision/session/${encodeURIComponent(sessionId)}/stop`;
        const body = JSON.stringify({ reason: String(reason || "browser_close") });

        // Prefer sendBeacon if available
        if (navigator?.sendBeacon) {
          const blob = new Blob([body], { type: "application/json" });
          navigator.sendBeacon(url, blob);
          return;
        }

        // Fallback: keepalive fetch (may still be dropped)
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch {
        // ignore
      }
    };

    const onBeforeUnload = (e) => {
      // show a confirm dialog if session is active and not already ending
      if (sessionEnded || endRequestedRef.current) return;
      if (!isActive && !isPaused) return;

      // Trigger best-effort end
      sendEndBeacon("browser_close");

      // Native browser confirm (cannot use custom modal here)
      e.preventDefault();
      e.returnValue = "Are you sure you want to end this session and leave?";
      return e.returnValue;
    };

    const onPageHide = () => {
      // Fires on mobile/background/tab close. Do not prompt; just end.
      if (sessionEnded || endRequestedRef.current) return;
      if (!isActive && !isPaused) return;
      sendEndBeacon("pagehide");
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [sessionId, sessionEnded, isActive, isPaused]);

  // Persistent sessions remain on this page when they become read-only.


  // Start grace countdown when modal opens
  useEffect(() => {
    if (!VISION_INACTIVITY_AUTO_END_ENABLED) return;
    if (!attentionModal.open) return;

    if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    if (graceIntervalRef.current) clearInterval(graceIntervalRef.current);
    graceTimerRef.current = null;
    graceIntervalRef.current = null;

    graceIntervalRef.current = setInterval(() => {
      setAttentionModal((s) => {
        if (!s.open) return s;
        const next = Math.max(0, Number(s.graceLeftSec || 0) - 1);
        return { ...s, graceLeftSec: next };
      });
    }, 1000);

    graceTimerRef.current = setTimeout(() => {
      stopSessionDirect({
        reason:
          attentionModal.kind === "paused"
            ? "auto_paused_timeout"
            : "auto_inactive_timeout",
      });
    }, GRACE_MS);

    return () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
      if (graceIntervalRef.current) clearInterval(graceIntervalRef.current);
      graceTimerRef.current = null;
      graceIntervalRef.current = null;
    };
  }, [attentionModal.open, attentionModal.kind, GRACE_MS, stopSessionDirect]);

  // Global “activity” listeners
  useEffect(() => {
    if (!VISION_INACTIVITY_AUTO_END_ENABLED) return;
    if (!sessionId) return;
    if (sessionEnded) return;

    const onKey = () => markActivity();
    const onDown = () => markActivity();
    const onWheel = () => markActivity();
    const onScroll = () => markActivity();
    const onTouch = () => markActivity();
    const onVis = () => {
      if (document.visibilityState === "visible") markActivity();
    };

    const onMove = () => {
      const now = Date.now();
      const last = mouseMoveThrottleRef.current || 0;
      if (now - last < 2000) return;
      mouseMoveThrottleRef.current = now;
      markActivity();
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchstart", onTouch);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [sessionId, sessionEnded, markActivity]);

  // Arm/clear inactivity + pause timers based on session status
  useEffect(() => {
    clearAttentionTimers();

    if (!VISION_INACTIVITY_AUTO_END_ENABLED) {
      closeAttentionModal();
      return;
    }

    if (!sessionId || sessionEnded) {
      closeAttentionModal();
      return;
    }

    if (isPaused) {
      pauseTimerRef.current = setTimeout(() => {
        setAttentionModal({
          open: true,
          kind: "paused",
          graceLeftSec: Math.ceil(GRACE_MS / 1000),
        });
      }, PAUSE_AFTER_MS);
      return;
    }

    if (isActive) {
      inactivityTimerRef.current = setTimeout(() => {
        setAttentionModal({
          open: true,
          kind: "inactivity",
          graceLeftSec: Math.ceil(GRACE_MS / 1000),
        });
      }, INACTIVE_AFTER_MS);
      return;
    }
  }, [
    sessionId,
    isPaused,
    isActive,
    sessionEnded,
    PAUSE_AFTER_MS,
    INACTIVE_AFTER_MS,
    GRACE_MS,
    clearAttentionTimers,
    closeAttentionModal,
  ]);

  // If we leave paused/active while modal open, close it
  useEffect(() => {
    if (!VISION_INACTIVITY_AUTO_END_ENABLED) return;
    if (!attentionModal.open) return;

    if (sessionEnded) {
      closeAttentionModal();
      return;
    }

    if (attentionModal.kind === "paused" && !isPaused) {
      closeAttentionModal();
      return;
    }

    if (attentionModal.kind === "inactivity" && !isActive) {
      closeAttentionModal();
      return;
    }
  }, [
    attentionModal.open,
    attentionModal.kind,
    isPaused,
    isActive,
    sessionEnded,
    closeAttentionModal,
  ]);

  const pauseSession = async () => {
    if (!sessionId || pauseBusy) return;
    if (sessionEnded) return;
    setPauseBusy(true);
    try {
      setNotice(null);
      await authedFetch(`/vision/session/${encodeURIComponent(sessionId)}/pause`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await fetchSnapshot();
      setNotice({ type: "ok", msg: "Session paused." });
    } catch (e) {
      setNotice({ type: "error", msg: e?.message || "Pause failed." });
    } finally {
      setPauseBusy(false);
    }
  };

  const resumeSession = async () => {
    if (!sessionId || pauseBusy) return;
    if (sessionEnded) return;
    setPauseBusy(true);
    try {
      setNotice(null);
      await authedFetch(`/vision/session/${encodeURIComponent(sessionId)}/resume`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await fetchSnapshot();
      setNotice({ type: "ok", msg: "Session resumed." });
      markActivity();
    } catch (e) {
      setNotice({ type: "error", msg: e?.message || "Resume failed." });
    } finally {
      setPauseBusy(false);
    }
  };

  const handleTogglePause = useCallback(async () => {
    if (!sessionId) return;
    if (sessionEnded) return;
    if (pauseBusy || endBusy) return;

    markActivity();

    if (isPaused) {
      await resumeSession();
    } else {
      await pauseSession();
    }
  }, [sessionId, sessionEnded, pauseBusy, endBusy, isPaused, markActivity]);

  const stopSession = async () => {
    // Finalize/End Session is intentionally unavailable in the current product.
    if (!VISION_AUTO_FINALIZE_ENABLED) return;
    if (endRequestedRef.current) return;
    if (endInFlightRef.current || endBusy) return;
    if (sessionEnded) return;

    openConfirm({
      title: "End session?",
      message: "End this session now? You won’t be able to send more messages afterward.",
      confirmText: "End session",
      onConfirm: async () => {
        endRequestedRef.current = true;
        closeConfirm();
        await stopSessionDirect({ reason: "user_confirmed_end" });
      },
    });
  };

  // =========================
  // ✅ Centralized analyze call
  // =========================
  const analyzeImageUrl = useCallback(
    async ({
      imageUrl = "",
      image_url = "",
      frame_id = "",
      frame_ids = [],
      target = "",
      session_doc_path = "",
      bucket = "",
      object_name = "",
      prompt = "",
      client_message_id = "",
      meta = {},
      silentIfBusy = false,
    } = {}) => {
      if (!sessionId) throw new Error("Missing session_id.");

      const snapNow =
        snapshotRef.current && typeof snapshotRef.current === "object"
          ? snapshotRef.current
          : {};
      const snapStatus = String(snapNow?.status || "").toLowerCase();

      if (sessionEnded) throw new Error("Session ended.");
      if (lowTimeMode) throw new Error("Less than a minute remains. Screen analysis is disabled.");
      if (snapStatus === "paused") throw new Error("Session is paused.");

      markActivity();

      const url = String(image_url || imageUrl || "").trim();
      const fid = String(frame_id || "").trim();
      const fids = Array.from(
        new Set(
          (Array.isArray(frame_ids) ? frame_ids : [])
            .map((x) => String(x || "").trim())
            .filter((x) => x && x !== "__LIVE__")
        )
      ).slice(0, MULTI_FRAME_MAX);
      const tgt = String(target || "").trim().toLowerCase();
      const sdp =
        String(session_doc_path || "").trim() ||
        String(snapNow?.session_doc_path || snapNow?.sessionDocPath || "").trim() ||
        "";

      if (!url && !fid && !fids.length) {
        throw new Error("Missing image_url, frame_id, or frame_ids.");
      }

      if (analyzeInFlightRef.current) {
        if (silentIfBusy) return { ok: true, skipped: true, reason: "analyze_in_flight" };
        throw new Error("Analyze already in progress.");
      }

      analyzeInFlightRef.current = true;

      try {
        const mode = "B"; // ✅ Force step-by-step
        const cmid = String(client_message_id || "").trim() || uuidLike();

        const includeLauncherThisCall = !launchMetaSentRef.current;

        const payload = {
          session_id: sessionId,
          ...(tgt ? { target: tgt } : {}),
          image_url: url || "",
          frame_id: fid || "",
          ...(fids.length ? { frame_ids: fids } : {}),
          session_doc_path: sdp || "",
          bucket: String(bucket || "").trim() || "",
          object_name: String(object_name || "").trim() || "",
          assist_mode: mode,
          client_message_id: cmid,
          ...(String(prompt || "").trim() ? { prompt: String(prompt || "").trim() } : {}),
          meta: buildMetaWithLauncherOnce({
            source: "screen_share",
            captured_at_ms: Date.now(),
            ...((meta && typeof meta === "object") ? meta : {}),
          }),
        };

        const analyzePath = buildAnalyzePath(sessionId);
        if (!analyzePath) throw new Error("Missing analyze path.");

        const out = await authedFetch(analyzePath, {
          method: "POST",
          body: JSON.stringify(payload),
        });

        if (includeLauncherThisCall && !(out && out.skipped)) {
          markLaunchMetaSent();
        }

        return out;
      } finally {
        analyzeInFlightRef.current = false;
      }
    },
    [
      sessionId,
      buildMetaWithLauncherOnce,
      markLaunchMetaSent,
      sessionEnded,
      lowTimeMode,
      markActivity,
    ]
  );

  // =========================
  // ✅ Screen share preview
  // =========================
  const startShare = async () => {
    setShareError(null);

    if (!navigator?.mediaDevices?.getDisplayMedia) {
      setShareError("Screen sharing is not supported in this browser.");
      return;
    }

    if (sessionEnded) return;
    if (isPaused) {
      setShareError("Resume the session to start sharing.");
      return;
    }

    try {
      markActivity();
      await screenShareRef.current?.startSharing?.();
    } catch (e) {
      setShareError(e?.message || "Failed to start screen share.");
    }
  };

  const stopShare = () => {
    openConfirm({
      title: "Stop sharing?",
      message: "Stop sharing your screen now?",
      confirmText: "Stop sharing",
      onConfirm: async () => {
        closeConfirm();
        try {
          await screenShareRef.current?.stopSharing?.("user_stop");
        } catch {}
        setIsSharing(false);
        setAnalyzeBusy(false);
        analyzeInFlightRef.current = false;
        setActiveFrameId(null);
        fetchFramesList({ force: true, silent: true }).catch(() => {});
      },
    });
  };

  // ✅ Manual Analyze button handler (Mode B only)
  const handleAnalyzeMyScreen = useCallback(async () => {
    if (analyzeBusy) return;
    if (sessionEnded) return;

    if (lowTimeMode) {
      setNotice({ type: "error", msg: "Less than a minute remains. Screen analysis is disabled." });
      return;
    }

    if (isPaused) {
      setNotice({ type: "error", msg: "Session is paused. Resume to analyze." });
      return;
    }

    const selectedId = String(activeFrameId || "").trim();
    if (selectedId) {
      try {
        setAnalyzeBusy(true);
        setNotice(null);
        markActivity();

        const fid = selectedId;
        const selectedSessionDocPath =
          String(
            snapshotRef.current?.session_doc_path ||
              snapshotRef.current?.sessionDocPath ||
              ""
          ).trim() || "";

        // ✅ Historical reanalysis is CANONICAL frame_id only.
        //
        // The preview may be displaying a temporary/signed URL, but the analyze
        // request must not send that URL together with the historical frame_id.
        // Sending both creates two possible target authorities. The backend must
        // hydrate the exact historical image from this canonical frame_id.
        //
        // This keeps the three product contracts distinct:
        //   historical single -> exact frame_id
        //   LIVE              -> fresh capture/upload below
        //   multi-screen      -> exact frame_ids in handleAnalyzeSelectedFrames
        setLatestFrame({
          image_url: "",
          frame_id: fid,
          session_doc_path: selectedSessionDocPath,
          bucket: "",
          object_name: "",
          access_kind: "",
          meta: buildMetaWithLauncherOnce({
            trigger: "manual_analyze_selected_frame_by_id",
            reason: "filmstrip_selected_frame",
            target_kind: "historical_single_frame",
            selected_frame_id: fid,
            captured_at_ms: Date.now(),
          }),
          raw: { frame_id: fid },
        });

        await analyzeImageUrl({
          image_url: "",
          frame_id: fid,
          target: "frame",
          session_doc_path: selectedSessionDocPath,
          prompt: "Reanalyze my screen",
          meta: buildMetaWithLauncherOnce({
            trigger: "manual_analyze_selected_frame_by_id",
            reason: "filmstrip_selected_frame",
            target_kind: "historical_single_frame",
            selected_frame_id: fid,
          }),
          silentIfBusy: false,
        });

        return;
      } catch (e) {
        setNotice({ type: "error", msg: e?.message || "Analyze failed." });
        return;
      } finally {
        setAnalyzeBusy(false);
      }
    }

    if (!isSharing) {
      setShareError("Start screen sharing first.");
      return;
    }

    try {
      setAnalyzeBusy(true);
      setNotice(null);
      markActivity();

      const up = await screenShareRef.current?.captureAndUpload?.({
        reason: "manual_analyze",
        meta: buildMetaWithLauncherOnce({ trigger: "analyze_button" }),
      });

      const frame = normalizeFrameIdentity(up);
      if (!frame.image_url && !frame.frame_id && !frame.local_url) {
        throw new Error("Upload succeeded but image_url/frame_id was missing.");
      }

      setLatestFrame({
        ...frame,
        meta: buildMetaWithLauncherOnce({
          ...(frame.meta && typeof frame.meta === "object" ? frame.meta : {}),
          trigger: "manual_analyze",
          captured_at_ms: Date.now(),
        }),
      });

      upsertFrameIntoFilmstrip(frame);
      fetchFramesList({ force: false, silent: true }).catch(() => {});

      await analyzeImageUrl({
        image_url: frame.image_url,
        frame_id: frame.frame_id,
        target: "latest",
        session_doc_path: frame.session_doc_path,
        bucket: frame.bucket,
        object_name: frame.object_name,
        prompt: "Analyze my screen",
        meta: buildMetaWithLauncherOnce({
          trigger: "manual_analyze",
          reason: "manual_analyze_button",
        }),
        silentIfBusy: false,
      });
    } catch (e) {
      setNotice({ type: "error", msg: e?.message || "Analyze failed." });
    } finally {
      setAnalyzeBusy(false);
    }
  }, [
    analyzeBusy,
    sessionEnded,
    lowTimeMode,
    isPaused,
    isSharing,
    activeFrameId,
    analyzeImageUrl,
    normalizeFrameIdentity,
    upsertFrameIntoFilmstrip,
    fetchFramesList,
    buildMetaWithLauncherOnce,
    markActivity,
  ]);

  // ✅ Explicit multi-frame analysis for 2–5 historical screens.
  // Sends canonical frame_ids only; Main/worker remain responsible for URL hydration/signing.
  const handleAnalyzeSelectedFrames = useCallback(async () => {
    if (analyzeBusy || sessionEnded || isPaused || lowTimeMode) return;

    const ids = Array.from(
      new Set(
        (Array.isArray(selectedFrameIds) ? selectedFrameIds : [])
          .map((x) => String(x || "").trim())
          .filter((x) => x && x !== "__LIVE__")
      )
    ).slice(0, MULTI_FRAME_MAX);

    if (ids.length < 2) {
      setNotice({ type: "error", msg: "Select at least 2 previous screens to analyze together." });
      return;
    }

    try {
      setAnalyzeBusy(true);
      setNotice(null);
      markActivity();

      await analyzeImageUrl({
        frame_ids: ids,
        session_doc_path:
          String(
            snapshotRef.current?.session_doc_path ||
              snapshotRef.current?.sessionDocPath ||
              ""
          ).trim() || "",
        prompt: "Analyze the selected screens together",
        meta: buildMetaWithLauncherOnce({
          trigger: "manual_analyze_selected_frames",
          reason: "filmstrip_multi_select",
          target_kind: "historical_multi_frame",
          frame_count: ids.length,
        }),
        silentIfBusy: false,
      });

      setSelectedFrameIds([]);
      setMultiSelectMode(false);
    } catch (e) {
      setNotice({ type: "error", msg: e?.message || "Multi-screen analysis failed." });
    } finally {
      setAnalyzeBusy(false);
    }
  }, [
    analyzeBusy,
    sessionEnded,
    isPaused,
    lowTimeMode,
    selectedFrameIds,
    analyzeImageUrl,
    buildMetaWithLauncherOnce,
    markActivity,
  ]);

  // ✅ auto analyze on new uploaded frames (Mode C) - kept but will never run because mode forced to B
  const handleFrameUploaded = useCallback(
    async (info) => {
      try {
        const mode = "B";
        if (mode !== "C") return;
        if (!isSharing) return;
        if (sessionEnded) return;
        if (isPaused) return;
        if (lowTimeMode) return;

        if (analyzeInFlightRef.current) return;

        markActivity();

        const t = Date.now();
        const last = lastAutoAnalyzeAtRef.current || 0;
        if (t - last < 1800) return;
        lastAutoAnalyzeAtRef.current = t;

        const frame = normalizeFrameIdentity(info);
        if (!frame.image_url && !frame.frame_id && !frame.local_url) return;

        setLatestFrame({
          ...frame,
          meta: buildMetaWithLauncherOnce({
            ...(frame.meta && typeof frame.meta === "object" ? frame.meta : {}),
            trigger: "auto_mode_c_frame_uploaded",
            captured_at_ms: Date.now(),
          }),
        });

        upsertFrameIntoFilmstrip(frame);
        fetchFramesList({ force: false, silent: true }).catch(() => {});

        await analyzeImageUrl({
          image_url: frame.image_url,
          frame_id: frame.frame_id,
          target: "latest",
          session_doc_path: frame.session_doc_path,
          bucket: frame.bucket,
          object_name: frame.object_name,
          prompt: "",
          meta: buildMetaWithLauncherOnce({
            trigger: "auto_mode_c",
            reason: frame?.meta?.reason || "",
            diff_score: frame?.meta?.diff_score ?? null,
          }),
          silentIfBusy: true,
        });
      } catch (e) {
        setNotice((prev) => prev || { type: "error", msg: e?.message || "Auto analyze failed." });
      }
    },
    [
      analyzeImageUrl,
      isSharing,
      sessionEnded,
      isPaused,
      lowTimeMode,
      normalizeFrameIdentity,
      upsertFrameIntoFilmstrip,
      fetchFramesList,
      buildMetaWithLauncherOnce,
      markActivity,
    ]
  );

  // ✅ Mode C Auto suggestion - kept but never runs because mode forced to B
  const handleAutoCaptureSuggested = useCallback(
    async (suggestion = {}) => {
      try {
        const mode = "B";
        if (mode !== "C") return;
        if (!isSharing) return;
        if (sessionEnded) return;
        if (isPaused) return;
        if (lowTimeMode) return;

        if (analyzeInFlightRef.current) return;

        markActivity();

        const t = Date.now();
        const last = lastAutoAnalyzeAtRef.current || 0;
        if (t - last < 1800) return;
        lastAutoAnalyzeAtRef.current = t;

        const up = await screenShareRef.current?.captureAndUpload?.({
          reason: suggestion?.why || suggestion?.reason || "auto_mode_c_suggested",
          meta: buildMetaWithLauncherOnce({
            trigger: "auto_mode_c_suggested",
            ...(suggestion?.meta && typeof suggestion.meta === "object" ? suggestion.meta : {}),
          }),
        });

        const frame = normalizeFrameIdentity(up);
        if (!frame.image_url && !frame.frame_id && !frame.local_url) return;

        setLatestFrame({
          ...frame,
          meta: buildMetaWithLauncherOnce({
            ...(frame.meta && typeof frame.meta === "object" ? frame.meta : {}),
            trigger: "auto_mode_c_suggested_capture",
            captured_at_ms: Date.now(),
          }),
        });

        upsertFrameIntoFilmstrip(frame);
        fetchFramesList({ force: false, silent: true }).catch(() => {});

        await analyzeImageUrl({
          image_url: frame.image_url,
          frame_id: frame.frame_id,
          target: "latest",
          session_doc_path: frame.session_doc_path,
          bucket: frame.bucket,
          object_name: frame.object_name,
          prompt: "",
          meta: buildMetaWithLauncherOnce({
            trigger: "auto_mode_c",
            suggested: true,
            why: suggestion?.why || suggestion?.reason || "",
            diff_score: suggestion?.diff_score ?? suggestion?.diffScore ?? null,
            ...(suggestion?.meta && typeof suggestion.meta === "object" ? suggestion.meta : {}),
          }),
          silentIfBusy: true,
        });
      } catch (e) {
        setNotice((prev) => prev || { type: "error", msg: e?.message || "Auto analyze failed." });
      }
    },
    [
      analyzeImageUrl,
      isSharing,
      sessionEnded,
      isPaused,
      lowTimeMode,
      normalizeFrameIdentity,
      upsertFrameIntoFilmstrip,
      fetchFramesList,
      buildMetaWithLauncherOnce,
      markActivity,
    ]
  );

  // ---------- Desktop split drag ----------
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const clientX = e?.clientX ?? (e?.touches && e.touches[0]?.clientX);
      if (!clientX) return;

      const wrap = document.getElementById("va_split_wrap");
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const pct = ((clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(20, Math.min(80, pct));
      setSplitPct(clamped);
    };

    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  // ---------- Desktop pane controls ----------
  const swapPanes = () => setPaneOrder((prev) => ({ left: prev.right, right: prev.left }));

  const minimizePane = (pane) => {
    if (!isDesktop) return;
    setCollapsed((prev) => ({ ...prev, [pane]: true }));
    setExpanded((prev) => (prev === pane ? null : prev));
  };

  const restorePane = (pane) => setCollapsed((prev) => ({ ...prev, [pane]: false }));

  const toggleExpand = (pane) => {
    if (!isDesktop) return;
    setCollapsed((prev) => ({ ...prev, [pane]: false }));
    setExpanded((prev) => (prev === pane ? null : pane));
  };

  // ✅ Prefer canonical paths returned by backend; fallback keeps current behavior intact
  const sessionDocPath =
    snapshot?.session_doc_path ||
    snapshot?.sessionDocPath ||
    (sessionId ? `vision_sessions/${sessionId}` : "");

  const messagesColPath =
    snapshot?.messages_col_path ||
    snapshot?.messagesColPath ||
    (sessionDocPath ? `${sessionDocPath}/messages` : "");

  // ✅ Unified screen helpers for VisionChatInterface
  const screenCaptureAndUpload = useCallback(async () => {
    if (sessionEnded) throw new Error("Session ended.");
    if (isPaused) throw new Error("Session is paused. Resume to capture.");
    if (lowTimeMode) throw new Error("Less than a minute remains. Uploads are disabled.");
    if (!isSharing) throw new Error("Start screen sharing first.");

    markActivity();

    const up = await screenShareRef.current?.captureAndUpload?.({
      reason: "chat_capture",
      meta: buildMetaWithLauncherOnce({ trigger: "chat_interface" }),
    });

    const frame = normalizeFrameIdentity(up);
    if (!frame.image_url && !frame.frame_id && !frame.local_url) {
      throw new Error("Upload succeeded but image_url/frame_id was missing.");
    }

    setLatestFrame({
      ...frame,
      meta: buildMetaWithLauncherOnce({
        ...(frame.meta && typeof frame.meta === "object" ? frame.meta : {}),
        trigger: "chat_capture",
        captured_at_ms: Date.now(),
      }),
    });

    upsertFrameIntoFilmstrip(frame);
    fetchFramesList({ force: false, silent: true }).catch(() => {});

    return { ...frame, raw: up };
  }, [
    sessionEnded,
    isPaused,
    lowTimeMode,
    isSharing,
    normalizeFrameIdentity,
    upsertFrameIntoFilmstrip,
    fetchFramesList,
    buildMetaWithLauncherOnce,
    markActivity,
  ]);

  const screenAnalyzeNow = useCallback(async () => {
    if (lowTimeMode) throw new Error("Less than a minute remains. Screen analysis is disabled.");
    const frame = await screenCaptureAndUpload();
    const out = await analyzeImageUrl({
      image_url: frame.image_url,
      frame_id: frame.frame_id,
      session_doc_path: frame.session_doc_path,
      bucket: frame.bucket,
      object_name: frame.object_name,
      prompt: "Analyze my screen",
      meta: buildMetaWithLauncherOnce({ trigger: "chat_analyze" }),
      silentIfBusy: false,
    });
    return {
      image_url: frame.image_url,
      frame_id: frame.frame_id,
      session_doc_path: frame.session_doc_path,
      analyze: out,
    };
  }, [screenCaptureAndUpload, analyzeImageUrl, buildMetaWithLauncherOnce, lowTimeMode]);

  // ✅ Chat input rules:
  // - Ended => disabled
  // - Paused => disabled
  // - Low-time => enabled (text-only)
  const chatDisabled = !canChatInput;

  const inputDisabledReason = useMemo(() => {
    if (sessionEnded) return "Session ended.";
    if (isPaused) return "Session is paused.";
    return "";
  }, [sessionEnded, isPaused]);

  const attachmentsDisabled = !canUpload;
  const attachmentsDisabledReason = useMemo(() => {
    if (sessionEnded) return "Session ended.";
    if (isPaused) return "Session is paused.";
    if (lowTimeMode) return "Less than a minute remains.";
    return "";
  }, [sessionEnded, isPaused, lowTimeMode]);

 
  
  
  
  
  const visionChatProps = useMemo(() => {
    return {
      sessionId,
      assistMode, // always "B"
      session: snapshot || null,
      launch: launchCtx,

      launcher: {
        sent: !!launchMetaSent,
        payload: launchCtx?.payload || null,
      },

      user: userCtx,
      firestorePaths: {
        sessionDoc: sessionDocPath,
        messagesCol: messagesColPath,
      },

      // Capability flags (child should enforce)
      capabilities: {
        sessionEnded: !!sessionEnded,
        lowTimeMode: !!lowTimeMode,
        canChatInput: !!canChatInput,
        canUpload: !!canUpload,
        canAnalyze: !!canAnalyze,
        remainingSeconds: typeof remainingSeconds === "number" ? remainingSeconds : null,
      },

      screen: {
        isSharing,
        captureAndUpload: screenCaptureAndUpload,
        analyzeWithCurrentScreenshot: screenAnalyzeNow,
        canUpload: !!canUpload,
        canAnalyze: !!canAnalyze,
      },

      filmstrip: {
        frames: framesRef.current || [],
        refresh: (opts = {}) => fetchFramesList({ force: true, silent: true, ...opts }),
        getFrameById,
        resolveFrameUrl,
        getVisionFrameUrl,
      },

      inputDisabled: chatDisabled,
      inputDisabledReason,

      // ✅ New flags for attachments in VisionChatInterface (you can wire these inside the component)
      attachmentsDisabled,
      attachmentsDisabledReason,

      printEnabled: true,
      allowPrintWhenInputDisabled: true,

      latestFrame: latestFrameRef.current || null,

      // Frontend-only screenshot -> chat navigation signal.
      // No backend/API contract changes are involved.
      frameAnchorRequest: chatFrameAnchorRequest,

      isBusy,
      busyMode,
    };
  }, [
    sessionId,
    assistMode,
    snapshot,
    launchCtx,
    launchMetaSent,
    userCtx,
    sessionDocPath,
    messagesColPath,
    sessionEnded,
    lowTimeMode,
    canChatInput,
    canUpload,
    canAnalyze,
    remainingSeconds,
    isSharing,
    screenCaptureAndUpload,
    screenAnalyzeNow,
    chatDisabled,
    inputDisabledReason,
    attachmentsDisabled,
    attachmentsDisabledReason,
    fetchFramesList,
    getFrameById,
    resolveFrameUrl,
    getVisionFrameUrl,
    chatFrameAnchorRequest,
    isBusy,
    busyMode,
  ]);

  // -------------------------
  // ✅ Low-time + Ended modals (one-time)
  // -------------------------
  const lowTimeShownRef = useRef(false);
  const endedShownRef = useRef(false);
  const [lowTimeModalOpen, setLowTimeModalOpen] = useState(false);
  const [endedModalOpen, setEndedModalOpen] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    try {
      lowTimeShownRef.current = sessionStorage.getItem(`va_low_time_modal_shown:${sessionId}`) === "1";
    } catch {}
    try {
      endedShownRef.current = sessionStorage.getItem(`va_ended_modal_shown:${sessionId}`) === "1";
    } catch {}
  }, [sessionId]);

  useEffect(() => {
    if (!VISION_TIMEOUT_NOTICES_ENABLED) {
      setLowTimeModalOpen(false);
      return;
    }
    if (!sessionId) return;
    if (sessionEnded) {
      setLowTimeModalOpen(false);
      return;
    }
    if (!lowTimeMode) return;
    if (lowTimeShownRef.current) return;

    lowTimeShownRef.current = true;
    try {
      sessionStorage.setItem(`va_low_time_modal_shown:${sessionId}`, "1");
    } catch {}

    setLowTimeModalOpen(true);
  }, [sessionId, lowTimeMode, sessionEnded]);

  useEffect(() => {
    if (!VISION_TIMEOUT_NOTICES_ENABLED) {
      setEndedModalOpen(false);
      return;
    }
    if (!sessionId) return;
    if (!sessionEnded) return;
    if (endedShownRef.current) return;

    endedShownRef.current = true;
    try {
      sessionStorage.setItem(`va_ended_modal_shown:${sessionId}`, "1");
    } catch {}

    setEndedModalOpen(true);
  }, [sessionId, sessionEnded]);

  // -------------------------
  // ✅ Visibility (NO unmounts)
  // -------------------------
  const previewVisible = isDesktop ? !collapsed.preview : activeTab === "preview";
  const chatVisible = isDesktop ? !collapsed.chat : activeTab === "chat";

  const showPreview = isDesktop ? (expanded ? expanded === "preview" : previewVisible) : previewVisible;
  const showChat = isDesktop ? (expanded ? expanded === "chat" : chatVisible) : chatVisible;

  const showDivider = isDesktop && !expanded && showPreview && showChat;

  const leftPct = Math.max(20, Math.min(80, splitPct));
  const rightPct = 100 - leftPct;
  const leftIsPreview = paneOrder.left === "preview";

  const previewPct = leftIsPreview ? leftPct : rightPct;
  const chatPct = leftIsPreview ? rightPct : leftPct;

  const previewOrder = leftIsPreview ? 0 : 2;
  const chatOrder = leftIsPreview ? 2 : 0;
  const dividerOrder = 1;

 const previewWrapStyle = isDesktop
  ? {
      ...styles.splitPaneWrap,
      display: showPreview ? "flex" : "none",
      order: previewOrder,
      width: expanded ? "100%" : showDivider ? `${previewPct}%` : "100%",
      flex: expanded ? "1 1 auto" : showDivider ? `0 0 ${previewPct}%` : "1 1 auto",
    }
  : {
      // ✅ Mobile/tab panels must be flex + minHeight:0 so children can scroll
      ...styles.splitPaneWrap,
      display: showPreview ? "flex" : "none",
      width: "100%",
      flex: "1 1 auto",
      minHeight: 0,
      height: "100%",
    };

const chatWrapStyle = isDesktop
  ? {
      ...styles.splitPaneWrap,
      display: showChat ? "flex" : "none",
      order: chatOrder,
      width: expanded ? "100%" : showDivider ? `${chatPct}%` : "100%",
      flex: expanded ? "1 1 auto" : showDivider ? `0 0 ${chatPct}%` : "1 1 auto",
    }
  : {
      ...styles.splitPaneWrap,
      display: showChat ? "flex" : "none",
      width: "100%",
      flex: "1 1 auto",
      minHeight: 0,
      height: "100%",
    };


  const dividerStyle = {
    ...styles.splitDivider,
    display: showDivider ? "flex" : "none",
    order: dividerOrder,
  };

  const showMinimizedDock = isDesktop && (collapsed.preview || collapsed.chat);

  // Reserve space so split panes never sit under corner buttons when top/bottom collapsed
  const CORNER_RESERVE_PX = 56;
  const splitPadTop = topCollapsed ? CORNER_RESERVE_PX : 0;
  const splitPadBottom = bottomCollapsed ? CORNER_RESERVE_PX : 0;

  // Overlays rendered by parent for now
  const SCREENSHARE_HANDLES_OVERLAYS = false;

  // ✅ UPDATED: Auto-assist never appears UI-wise
  const isAutoAssist = false;
  // Assistance Mode remains internally locked to B; no selector is rendered in the current UI.

  // ✅ NEW: button label driven by parent busyMode (local analyze still wins)
  // Analyze-button label is derived after active filmstrip item resolution.

  // Filmstrip items include LIVE tile only while sharing
  const filmstripItems = useMemo(() => {
    const list = Array.isArray(frames) ? frames.filter((x) => x && x.frame_id) : [];
    const out = [...list];
    if (isSharing) {
      out.push({
        kind: "live",
        frame_id: "__LIVE__",
        thumb_url: "",
        image_url: "",
        image_proxy_url: "",
        local_url: "",
        local_thumb_url: "",
        at_ms: Date.now(),
        raw: {},
      });
    }
    return out;
  }, [frames, isSharing]);

  // ✅ Active item logic
  const activeItemId = useMemo(() => {
    const sel = String(activeFrameId || "").trim();
    if (isSharing) return sel || "__LIVE__";
    if (sel) return sel;
    const list = Array.isArray(frames) ? frames : [];
    return list.length ? String(list[list.length - 1]?.frame_id || "") : "";
  }, [activeFrameId, isSharing, frames]);

  const activeIndex = useMemo(() => {
    if (!filmstripItems.length) return 0;
    const idx = filmstripItems.findIndex((x) => String(x?.frame_id || "") === String(activeItemId || ""));
    return idx >= 0 ? idx : filmstripItems.length - 1;
  }, [filmstripItems, activeItemId]);

  const activeItem = useMemo(() => filmstripItems[activeIndex] || null, [filmstripItems, activeIndex]);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < filmstripItems.length - 1;

  const goPrev = useCallback(() => {
    if (!canPrev) return;
    const nextIdx = Math.max(0, activeIndex - 1);
    const it = filmstripItems[nextIdx];
    if (!it) return;

    if (it.kind === "live" || it.frame_id === "__LIVE__") {
      setActiveFrameId(null);
    } else {
      setActiveFrameId(it.frame_id);
      requestChatFrameAnchor(it.frame_id);
    }

    markActivity();
  }, [
    canPrev,
    activeIndex,
    filmstripItems,
    requestChatFrameAnchor,
    markActivity,
  ]);

  const goNext = useCallback(() => {
    if (!canNext) return;
    const nextIdx = Math.min(filmstripItems.length - 1, activeIndex + 1);
    const it = filmstripItems[nextIdx];
    if (!it) return;

    if (it.kind === "live" || it.frame_id === "__LIVE__") {
      setActiveFrameId(null);
    } else {
      setActiveFrameId(it.frame_id);
      requestChatFrameAnchor(it.frame_id);
    }

    markActivity();
  }, [
    canNext,
    activeIndex,
    filmstripItems,
    requestChatFrameAnchor,
    markActivity,
  ]);

  // fetch filmstrip when sharing starts
  useEffect(() => {
    if (!sessionId) return;
    if (!isSharing) return;
    if (sessionEnded) return;
    fetchFramesList({ force: true, silent: true }).catch(() => {});
  }, [sessionId, isSharing, fetchFramesList, sessionEnded]);

  // keyboard arrows for filmstrip
  useEffect(() => {
    if (!showPreview) return;
    if (filmstripItems.length < 2) return;

    const onKeyDown = (e) => {
      const key = e?.key || "";
      if (key === "ArrowLeft") {
        if (canPrev) {
          e.preventDefault();
          goPrev();
        }
      } else if (key === "ArrowRight") {
        if (canNext) {
          e.preventDefault();
          goNext();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showPreview, filmstripItems.length, canPrev, canNext, goPrev, goNext]);

  const showingSelectedFrame = !!activeItem && activeItem.kind === "frame";

  const analyzeBtnText =
    busyMode === "analyzing"
      ? "Analyzing…"
      : busyMode === "thinking"
      ? "Thinking…"
      : showingSelectedFrame
      ? "Reanalyze my screen"
      : "Analyze my screen";

  // ✅ Analyze overlay visibility:
  // - disabled in ended/paused/low-time
  // - requires step-by-step mode
  // - requires sharing OR a selected frame
  const showAnalyzeOverlay =
    !multiSelectMode &&
    !!canAnalyze &&
    String(assistMode || "B").toUpperCase() === "B" &&
    (!!isSharing || !!showingSelectedFrame);

  // The expanded/mobile Chat navigator is always single-frame navigation, so
  // its explicit Analyze/Reanalyze action does not inherit Preview multi-select UI state.
  const showChatAnalyzeAction =
    !!canAnalyze &&
    String(assistMode || "B").toUpperCase() === "B" &&
    (!!isSharing || !!showingSelectedFrame);

  // ✅ Chat-side screen navigator:
  // - desktop: visible only when Vision Chat itself is expanded
  // - mobile: visible on the Chat tab, since mobile intentionally uses tabs
  //   instead of desktop split/expand controls
  const showChatFilmstrip =
    filmstripItems.length > 0 &&
    (
      chatOnlyView ||
      (isDesktop && expanded === "chat") ||
      (!isDesktop && activeTab === "chat")
    );

  // ✅ helper: choose best visible src for a frame tile (local first, then cache, then server)
  const getBestThumbSrc = useCallback((it) => {
    const fid = String(it?.frame_id || "").trim();
    const cache = (fid && frameUrlCacheRef.current && frameUrlCacheRef.current[fid]) || null;

    const localThumb = String(cache?.local_thumb_url || "").trim() || String(it?.local_thumb_url || "").trim();
    const serverThumb = String(cache?.thumb_url || "").trim() || String(it?.thumb_url || "").trim();
    const localFull = String(cache?.local_url || "").trim() || String(it?.local_url || "").trim();
    const serverFull = String(cache?.image_url || "").trim() || String(it?.image_url || "").trim();
    const proxyFull =
      String(cache?.image_proxy_url || "").trim() ||
      String(it?.image_proxy_url || "").trim();

    return localThumb || serverThumb || localFull || serverFull || proxyFull || "";
  }, []);

  const getBestImageSrc = useCallback((it) => {
    const fid = String(it?.frame_id || "").trim();
    const cache = (fid && frameUrlCacheRef.current && frameUrlCacheRef.current[fid]) || null;

    const localFull = String(cache?.local_url || "").trim() || String(it?.local_url || "").trim();
    const serverFull = String(cache?.image_url || "").trim() || String(it?.image_url || "").trim();
    const proxyFull =
      String(cache?.image_proxy_url || "").trim() ||
      String(it?.image_proxy_url || "").trim();
    const localThumb = String(cache?.local_thumb_url || "").trim() || String(it?.local_thumb_url || "").trim();
    const serverThumb = String(cache?.thumb_url || "").trim() || String(it?.thumb_url || "").trim();

    return localFull || serverFull || proxyFull || localThumb || serverThumb || "";
  }, []);

  const selectedFrameUrl = showingSelectedFrame ? getBestImageSrc(activeItem) : "";

  // ✅ Navigation hydration / prefetch.
  //
  // When a historical frame becomes active:
  //   1) keep the selected frame fresh as a full image
  //   2) pre-hydrate only its immediate previous/next historical neighbors
  //      as thumbnails
  //
  // Example with 20 frames:
  // selecting frame #10 hydrates #10 first, then #9 and #11 in the background.
  // We do NOT sign all 20 frames and we do NOT create a timer/poller.
  useEffect(() => {
    const it = activeItem;
    if (!it || it.kind !== "frame") return undefined;

    const activeFid = String(it.frame_id || "").trim();
    if (!activeFid) return undefined;

    const historical = (Array.isArray(frames) ? frames : []).filter(
      (x) => x && String(x.frame_id || "").trim()
    );
    const currentIndex = historical.findIndex(
      (x) => String(x?.frame_id || "").trim() === activeFid
    );
    if (currentIndex < 0) return undefined;

    const orderedTargets = [];

    // Active frame always goes first so the visible preview gets priority.
    orderedTargets.push({
      item: historical[currentIndex],
      prefer: "image",
    });

    // Then warm the immediate neighbors only.
    for (let distance = 1; distance <= FRAME_URL_NAV_PREFETCH_RADIUS; distance += 1) {
      const prev = historical[currentIndex - distance];
      const next = historical[currentIndex + distance];

      if (prev) orderedTargets.push({ item: prev, prefer: "thumb" });
      if (next) orderedTargets.push({ item: next, prefer: "thumb" });
    }

    let cancelled = false;

    (async () => {
      for (const target of orderedTargets) {
        if (cancelled) return;

        const targetItem = target?.item || null;
        const fid = String(targetItem?.frame_id || "").trim();
        if (!fid) continue;

        const hydratedAt = Number(frameUrlHydratedAtRef.current?.[fid] || 0);
        const hasVisibleUrl =
          target.prefer === "image"
            ? !!getBestImageSrc(targetItem)
            : !!getBestThumbSrc(targetItem);

        const stale =
          !hydratedAt ||
          Date.now() - hydratedAt >= FRAME_URL_REHYDRATE_AFTER_MS;

        if (hasVisibleUrl && !stale) continue;

        await getVisionFrameUrl(fid, {
          prefer: target.prefer,
          forceRefresh: true,
        }).catch(() => "");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeItem,
    frames,
    getBestImageSrc,
    getBestThumbSrc,
    getVisionFrameUrl,
  ]);

  // ✅ Selected-frame error recovery. A broken source invalidates every remote
  // candidate for that frame and forces a true backend re-sign by frame_id.
  const selectedRetryRef = useRef({});

  const handleSelectedImgError = useCallback(
    (frameId) => {
      const fid = String(frameId || "").trim();
      if (!fid) return;

      const m = selectedRetryRef.current || {};
      const tries = Number(m[fid] || 0) || 0;
      if (tries >= 2) return;
      selectedRetryRef.current = { ...m, [fid]: tries + 1 };

      frameUrlHydratedAtRef.current = {
        ...(frameUrlHydratedAtRef.current || {}),
        [fid]: 0,
      };

      setFrames((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((x) =>
          x && String(x.frame_id || "") === fid
            ? { ...x, image_url: "", image_proxy_url: "", thumb_url: "", local_url: "", local_thumb_url: "" }
            : x
        );
      });

      setFrameUrlCache((prev) => {
        const p = prev && typeof prev === "object" ? prev : {};
        const cur = p[fid] && typeof p[fid] === "object" ? p[fid] : {};
        return {
          ...p,
          [fid]: {
            ...cur,
            image_url: "",
            image_proxy_url: "",
            thumb_url: "",
            local_url: "",
            local_thumb_url: "",
            hydrated_at_ms: 0,
          },
        };
      });

      getVisionFrameUrl(fid, { prefer: "image", forceRefresh: true }).catch(() => {});
    },
    [getVisionFrameUrl]
  );

  // ✅ Thumbnail error recovery. The thumbnail may actually be a full-image
  // fallback, so clear both remote candidates before asking Main to re-sign.
  const thumbRetryRef = useRef({});
  const handleThumbImgError = useCallback(
    (frameId) => {
      const fid = String(frameId || "").trim();
      if (!fid) return;

      const m = thumbRetryRef.current || {};
      const tries = Number(m[fid] || 0) || 0;
      if (tries >= 2) return;
      thumbRetryRef.current = { ...m, [fid]: tries + 1 };

      frameUrlHydratedAtRef.current = {
        ...(frameUrlHydratedAtRef.current || {}),
        [fid]: 0,
      };

      setFrames((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((x) =>
          x && String(x.frame_id || "") === fid
            ? { ...x, thumb_url: "", image_url: "", image_proxy_url: "", local_thumb_url: "", local_url: "" }
            : x
        );
      });

      setFrameUrlCache((prev) => {
        const p = prev && typeof prev === "object" ? prev : {};
        const cur = p[fid] && typeof p[fid] === "object" ? p[fid] : {};
        return {
          ...p,
          [fid]: {
            ...cur,
            thumb_url: "",
            image_url: "",
            image_proxy_url: "",
            local_thumb_url: "",
            local_url: "",
            hydrated_at_ms: 0,
          },
        };
      });

      getVisionFrameUrl(fid, { prefer: "thumb", forceRefresh: true }).catch(() => {});
    },
    [getVisionFrameUrl]
  );

  // ===== Modal actions (inactivity / paused) =====
  const attentionExtraLine =
    attentionModal.open
      ? `Auto-ending in ${Math.max(0, Number(attentionModal.graceLeftSec || 0))}s…`
      : "";

  const onAttentionLeft = async () => {
    if (attentionModal.kind === "inactivity") {
      closeAttentionModal();
      markActivity();
      return;
    }

    if (attentionModal.kind === "paused") {
      closeAttentionModal();
      await resumeSession();
      return;
    }
  };

  const onAttentionRight = async () => {
    closeAttentionModal();
    await stopSessionDirect({
      reason: attentionModal.kind === "paused" ? "user_end_from_pause_modal" : "user_end_from_inactivity_modal",
    });
  };

  // -------------------------
  // ✅ Compact Chat frame navigator
  // -------------------------
  // Expanded desktop Chat and historical chat-only both use the same frame rail.
  // LIVE remains the last tile because filmstripItems appends it last.
  // Historical clicks select the frame and request chat anchoring; Analyze/Reanalyze
  // stays an explicit separate action.
  const renderCompactChatNavigator = ({ showPaneControls = false } = {}) => (
    <div
      style={{
        ...styles.chatNavigatorHeader,
        ...(!isDesktop ? styles.chatNavigatorHeaderMobile : null),
      }}
    >
      <div
        style={{
          ...styles.chatNavigatorTrack,
          ...(!isDesktop ? styles.chatNavigatorTrackMobile : null),
        }}
      >
        <button
          type="button"
          style={{
            ...styles.chatNavArrow,
            ...(!canPrev ? styles.chatNavArrowDisabled : null),
          }}
          onClick={goPrev}
          disabled={!canPrev}
          title="Previous screen"
          aria-label="Previous screen"
        >
          ‹
        </button>

        <div
          style={{
            ...styles.chatNavigatorRail,
            ...(!isDesktop ? styles.chatNavigatorRailMobile : null),
          }}
        >
          {filmstripItems.map((it) => {
            const id = String(it?.frame_id || "");
            const isActiveTile = id === String(activeItemId || "");

            if (it?.kind === "live" || id === "__LIVE__") {
              return (
                <button
                  key={`chat-nav-${id}`}
                  type="button"
                  style={{
                    ...styles.thumbItem,
                    ...(isDesktop ? styles.chatNavThumbDesktop : styles.thumbItemMobile),
                    ...(isActiveTile ? styles.thumbItemActive : null),
                    ...styles.thumbItemLive,
                  }}
                  onClick={() => {
                    setActiveFrameId(null);
                    markActivity();
                  }}
                  title="Live screen"
                >
                  LIVE
                </button>
              );
            }

            const src = getBestThumbSrc(it);

            return (
              <button
                key={`chat-nav-${id}`}
                type="button"
                style={{
                  ...styles.thumbItem,
                  ...(isDesktop ? styles.chatNavThumbDesktop : styles.thumbItemMobile),
                  ...(isActiveTile ? styles.thumbItemActive : null),
                }}
                onClick={() => {
                  setActiveFrameId(id);
                  requestChatFrameAnchor(id);
                  markActivity();
                }}
                title="Open this screen's latest single-screen analysis"
              >
                {src ? (
                  <img
                    src={src}
                    alt=""
                    style={styles.thumbImg}
                    draggable={false}
                    onError={() => handleThumbImgError(id)}
                  />
                ) : (
                  <div style={styles.thumbFallback}>Frame</div>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          style={{
            ...styles.chatNavArrow,
            ...(!canNext ? styles.chatNavArrowDisabled : null),
          }}
          onClick={goNext}
          disabled={!canNext}
          title="Next screen"
          aria-label="Next screen"
        >
          ›
        </button>
      </div>

      <div
        style={{
          ...styles.chatNavigatorActions,
          ...(!isDesktop ? styles.chatNavigatorActionsMobile : null),
        }}
      >
        {showChatAnalyzeAction ? (
          <button
            type="button"
            style={{
              ...styles.btn,
              ...styles.btnSmall,
              ...(analyzeBusy ? styles.analyzeBtnBusy : null),
            }}
            onClick={handleAnalyzeMyScreen}
            disabled={analyzeBusy || !canAnalyze}
            title={
              sessionEnded
                ? "Session ended"
                : isPaused
                ? "Session paused"
                : lowTimeMode
                ? "Less than a minute remains"
                : showingSelectedFrame
                ? "Reanalyze this previous screen"
                : "Analyze the live screen"
            }
          >
            {analyzeBtnText}
          </button>
        ) : null}

        {showPaneControls && isDesktop ? (
          <>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSmall }}
              onClick={swapPanes}
            >
              Swap
            </button>

            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSmall }}
              onClick={() => minimizePane("chat")}
              title="Minimize this pane"
            >
              Minimize
            </button>

            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSmall }}
              onClick={() => toggleExpand("chat")}
              title="Return to split view"
            >
              Split view
            </button>
          </>
        ) : null}
      </div>
    </div>
  );

  // -------------------------
  // ✅ Chat-only page render
  // -------------------------
  const chatOnlyRender = (
    <div style={{ ...styles.outerWrap, ...(isDesktop ? null : styles.outerWrapMobile) }}>
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        onCancel={closeConfirm}
        onConfirm={confirmState.onConfirm || closeConfirm}
      />

      <OneActionModal
        open={VISION_TIMEOUT_NOTICES_ENABLED && lowTimeModalOpen}
        title="Almost out of time"
        message="You have less than a minute left. You can keep chatting, but uploads and screen analysis are disabled so your session can wrap up smoothly."
        buttonText="Got it"
        onClose={() => setLowTimeModalOpen(false)}
      />

      <TwoActionModal
        open={VISION_TIMEOUT_NOTICES_ENABLED && endedModalOpen}
        title="Session ended"
        message="This session is closed. You can still read this conversation, but you can’t send new messages or run analysis."
        extraLine="To continue, add more Vision minutes and start a new session."
        leftText="Close"
        rightText="Add more minutes"
        leftVariant="ghost"
        rightVariant="default"
        onLeft={() => setEndedModalOpen(false)}
        onRight={() => {
          setEndedModalOpen(false);
          navigate("/vision-assist");
        }}
      />

      <TwoActionModal
        open={VISION_INACTIVITY_AUTO_END_ENABLED && attentionModal.open}
        title={attentionModal.kind === "paused" ? "Session paused" : "Still there?"}
        message={
          attentionModal.kind === "paused"
            ? "This session has been paused for 10 minutes. Resume or end the session?"
            : "You’ve been inactive for 5 minutes. Continue the session?"
        }
        extraLine={
          attentionModal.open
            ? `Auto-ending in ${Math.max(0, Number(attentionModal.graceLeftSec || 0))}s…`
            : ""
        }
        leftText={attentionModal.kind === "paused" ? "Resume" : "Continue"}
        rightText="End session"
        leftVariant="ghost"
        rightVariant="danger"
        onLeft={onAttentionLeft}
        onRight={onAttentionRight}
        leftDisabled={pauseBusy || endBusy}
        rightDisabled={endBusy}
      />

     <div style={{ ...styles.card, ...(isDesktop ? null : styles.cardMobile) }}>

        {loading && <div style={styles.noticeMuted}>Loading session…</div>}

        {notice && (
          <div
            style={{
              ...styles.notice,
              ...(notice.type === "ok" ? styles.noticeOk : styles.noticeErr),
            }}
          >
            {notice.msg}
          </div>
        )}

        <div style={{ ...styles.panel, flex: "1 1 auto", minHeight: 0 }}>
          {showChatFilmstrip ? (
            renderCompactChatNavigator({ showPaneControls: false })
          ) : (
            <div style={styles.panelHeader}>
              <div style={styles.panelTitle}>Vision chat</div>
            </div>
          )}

          <div style={styles.chatWrap}>
            <VisionChatInterface {...visionChatProps} />
          </div>
        </div>
      </div>
    </div>
  );

  // -------------------------
  // ✅ Main split render
  // -------------------------
  const mainRender = (
    <div style={{ ...styles.outerWrap, ...(isDesktop ? null : styles.outerWrapMobile) }}>
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        onCancel={closeConfirm}
        onConfirm={confirmState.onConfirm || closeConfirm}
      />

      <OneActionModal
        open={VISION_TIMEOUT_NOTICES_ENABLED && lowTimeModalOpen}
        title="Almost out of time"
        message="You have less than a minute left. You can keep chatting, but uploads and screen analysis are disabled so your session can wrap up smoothly."
        buttonText="Got it"
        onClose={() => setLowTimeModalOpen(false)}
        extraLine={
          typeof remainingSeconds === "number"
            ? `Time left: ${Math.max(0, remainingSeconds)}s`
            : ""
        }
      />

      <TwoActionModal
        open={VISION_TIMEOUT_NOTICES_ENABLED && endedModalOpen}
        title="Session ended"
        message="This session is closed. You can still read this conversation, but you can’t send new messages or run analysis."
        extraLine="To continue, add more Vision minutes and start a new session."
        leftText="Close"
        rightText="Add more minutes"
        leftVariant="ghost"
        rightVariant="default"
        onLeft={() => setEndedModalOpen(false)}
        onRight={() => {
          setEndedModalOpen(false);
          navigate("/vision-assist");
        }}
      />

      <TwoActionModal
        open={VISION_INACTIVITY_AUTO_END_ENABLED && attentionModal.open}
        title={attentionModal.kind === "paused" ? "Session paused" : "Still there?"}
        message={
          attentionModal.kind === "paused"
            ? "This session has been paused for 10 minutes. Resume or end the session?"
            : "You’ve been inactive for 5 minutes. Continue the session?"
        }
        extraLine={attentionExtraLine}
        leftText={attentionModal.kind === "paused" ? "Resume" : "Continue"}
        rightText="End session"
        leftVariant="ghost"
        rightVariant="danger"
        onLeft={onAttentionLeft}
        onRight={onAttentionRight}
        leftDisabled={pauseBusy || endBusy}
        rightDisabled={endBusy}
      />

      <div style={{ ...styles.card, ...(isDesktop ? null : styles.cardMobile) }}>

        {!topCollapsed ? (
          <>
            <div style={styles.headerRow}>
              <div style={styles.headerTitleWrap}>
                <h1 style={styles.title}>Vision Assist Session</h1>
              </div>

              <div
                style={{
                  ...styles.headerActions,
                  ...(isDesktop ? null : styles.headerActionsMobile),
                }}
              >
                <VisionAssistClockControls
                  styles={styles}
                  status={status}
                  isPaused={isPaused}
                  isCompleted={isCompleted}
                  hardLocked={sessionEnded}
                  sessionId={sessionId}
                  remainingSeconds={remainingSeconds}
                  isBusy={isBusy}
                  pauseBusy={pauseBusy}
                  endBusy={endBusy}
                  onTogglePause={handleTogglePause}
                  // End/Finalize/AutoEnd intentionally not exposed.
                />

                <button
                  type="button"
                  style={styles.headerToggleBtn}
                  onClick={() => setTopCollapsed(true)}
                  title="Hide top section"
                >
                  Hide
                </button>
              </div>
            </div>

            {loading && <div style={styles.noticeMuted}>Loading session…</div>}

            {notice && (
              <div
                style={{
                  ...styles.notice,
                  ...(notice.type === "ok" ? styles.noticeOk : styles.noticeErr),
                }}
              >
                {notice.msg}
              </div>
            )}

            {isPaused && !sessionEnded ? (
              <div style={{ ...styles.notice, ...styles.noticeErr, marginTop: 10 }}>
                <strong>Session paused:</strong> Chat + analyzing are disabled until you resume.
              </div>
            ) : null}

            {VISION_TIMEOUT_NOTICES_ENABLED && lowTimeMode && !sessionEnded && !isPaused ? (
              <div style={{ ...styles.notice, ...styles.noticeMuted, marginTop: 10 }}>
                <strong>Chat-only mode:</strong> Uploads and screen analysis are disabled while you have less than a minute remaining.
              </div>
            ) : null}

            {!isDesktop ? (
              <div style={styles.mobileTabs}>
                <button
                  type="button"
                  style={{
                    ...styles.tabBtn,
                    ...(activeTab === "preview" ? styles.tabBtnActive : null),
                  }}
                  onClick={() => setActiveTab("preview")}
                >
                  Preview
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.tabBtn,
                    ...(activeTab === "chat" ? styles.tabBtnActive : null),
                  }}
                  onClick={() => setActiveTab("chat")}
                >
                  Chat
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <button
            type="button"
            style={styles.topCornerToggle}
            onClick={() => setTopCollapsed(false)}
            title="Show top section"
          >
            Show
          </button>
        )}

        <div
          id="va_split_wrap"
          style={{
            ...styles.splitWrap,
            marginTop: topCollapsed ? 0 : styles.splitWrap.marginTop,
            paddingTop: splitPadTop,
            paddingBottom: splitPadBottom,
            ...(isDesktop ? null : styles.splitWrapMobile),
          }}
        >
          {/* PREVIEW */}
          <div style={previewWrapStyle} aria-hidden={!showPreview}>
            <div style={styles.panel}>
              <div style={styles.panelHeader}>
                <div style={styles.panelTitle}>Shared screen (preview)</div>

                {isDesktop ? (
                  <div style={styles.panelActions}>
                    <button type="button" style={{ ...styles.btn, ...styles.btnSmall }} onClick={swapPanes}>
                      Swap
                    </button>

                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnSmall }}
                      onClick={() => minimizePane("preview")}
                      title="Minimize this pane"
                    >
                      Minimize
                    </button>

                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnSmall }}
                      onClick={() => toggleExpand("preview")}
                      title={expanded === "preview" ? "Return to split view" : "Expand to full view"}
                    >
                      {expanded === "preview" ? "Split view" : "Expand"}
                    </button>
                  </div>
                ) : null}
              </div>

              <div style={styles.panelHeaderSub}>
                <div style={styles.panelTitleSub}>{!isSharing ? "Not sharing" : "Sharing active"}</div>
                <div style={styles.panelActions}>
                  {!isSharing ? (
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnSmall }}
                      onClick={startShare}
                      disabled={sessionEnded || isPaused}
                    >
                      Select tab/window
                    </button>
                  ) : (
                    <button type="button" style={{ ...styles.btn, ...styles.btnSmall }} onClick={stopShare}>
                      Stop sharing
                    </button>
                  )}
                </div>
              </div>

              {shareError && <div style={styles.noticeErrInline}>{shareError}</div>}

              <div
                style={{
                  ...styles.previewBody,
                  ...(!isDesktop ? styles.previewBodyMobile : null),
                }}
              >
                <div style={styles.videoWrapFlex}>
                  <VisionAssistScreenShare
                    ref={screenShareRef}
                    sessionId={sessionId}
                    assistMode={assistMode}
                    isLiveAssist={false}
                    viewMode={launchCtx?.payload?.viewMode || "split"}
                    disabled={sessionEnded || isPaused}
                    frameUploadPath={buildFrameUploadPath(sessionId)}
                    autoSendAnalyze={false}
                    showStatus={false}
                    isBusy={isBusy}
                    busyMode={busyMode}
                    showAnalyzeButton={false}
                    analyzeBusy={false}
                    analyzeButtonText=""
                    onAnalyzeMyScreen={undefined}
                    showModeCHint={false}
                    modeCHintText=""
                    onSharingChange={(v) => {
                      const on = !!v;
                      setIsSharing(on);
                      markActivity();

                      if (!on) setAnalyzeBusy(false);
                      if (!on) analyzeInFlightRef.current = false;

                      setActiveFrameId(null);
                      fetchFramesList({ force: true, silent: true }).catch(() => {});
                    }}
                    onError={(err) => {
                      setShareError(err?.message || "Screen share failed.");
                      setIsSharing(false);
                      analyzeInFlightRef.current = false;
                    }}
                    onFrameUploaded={handleFrameUploaded}
                    onAutoCaptureSuggested={handleAutoCaptureSuggested}
                    style={{ width: "100%", height: "100%" }}
                    videoStyle={styles.video}
                  />

                 {!SCREENSHARE_HANDLES_OVERLAYS && showingSelectedFrame && !!selectedFrameUrl ? (
  <img
    src={selectedFrameUrl}
    alt="Selected frame"
    style={styles.selectedFrameImg}
    draggable={false}
    onError={() => handleSelectedImgError(activeItem?.frame_id)}
  />
) : null}


                  {!SCREENSHARE_HANDLES_OVERLAYS && !isSharing && !showingSelectedFrame && (
                    <div style={styles.videoOverlay}>
                      Click <strong>Select tab/window</strong> to choose what you want to share.
                    </div>
                  )}

                  {/* ✅ Analyze my screen overlay button (disabled in low-time/paused/ended) */}
                  {!SCREENSHARE_HANDLES_OVERLAYS && showAnalyzeOverlay ? (
                    <div style={styles.analyzeOverlay}>
                      <button
                        type="button"
                        style={{
                          ...styles.btn,
                          ...styles.analyzeBtn,
                          ...(analyzeBusy ? styles.analyzeBtnBusy : null),
                        }}
                        onClick={handleAnalyzeMyScreen}
                        disabled={analyzeBusy || !canAnalyze}
                        title={
                          sessionEnded
                            ? "Session ended"
                            : isPaused
                            ? "Session paused"
                            : lowTimeMode
                            ? "Less than a minute remains"
                            : "Analyze"
                        }
                      >
                        {analyzeBtnText}
                      </button>
                    </div>
                  ) : null}

                  {!SCREENSHARE_HANDLES_OVERLAYS && filmstripItems.length > 1 ? (
                    <>
                      {canPrev ? (
                        <button
                          type="button"
                          style={{ ...styles.navArrowBtn, ...styles.navArrowLeft }}
                          onClick={goPrev}
                          title="Previous"
                        >
                          ‹
                        </button>
                      ) : null}

                      {canNext ? (
                        <button
                          type="button"
                          style={{ ...styles.navArrowBtn, ...styles.navArrowRight }}
                          onClick={goNext}
                          title="Next"
                        >
                          ›
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>

                {/* ✅ Filmstrip: single-frame by default, explicit 2–5 multi-select on demand */}
                {filmstripItems.length ? (
                  <div
                    style={{
                      ...styles.filmstripWrap,
                      ...(!isDesktop ? styles.filmstripWrapMobile : null),
                    }}
                  >
                    <div
                      style={{
                        ...styles.filmstripToolbar,
                        ...(!isDesktop ? styles.filmstripToolbarMobile : null),
                      }}
                    >
                      <div style={styles.filmstripLabel}>
                        {multiSelectMode
                          ? `${selectedFrameIds.length} / ${MULTI_FRAME_MAX} selected`
                          : "Previous screens"}
                      </div>

                      <div style={styles.filmstripActions}>
                        {multiSelectMode ? (
                          <>
                            <button
                              type="button"
                              style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnGhost }}
                              onClick={clearMultiSelection}
                              disabled={analyzeBusy}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              style={{
                                ...styles.btn,
                                ...styles.btnSmall,
                                ...(selectedFrameIds.length < 2 ? styles.btnDisabled : null),
                              }}
                              onClick={handleAnalyzeSelectedFrames}
                              disabled={analyzeBusy || selectedFrameIds.length < 2}
                              title={
                                selectedFrameIds.length < 2
                                  ? "Select at least 2 previous screens"
                                  : `Analyze ${selectedFrameIds.length} selected screens`
                              }
                            >
                              {analyzeBusy
                                ? "Analyzing…"
                                : selectedFrameIds.length >= 2
                                ? `Analyze ${selectedFrameIds.length} screens`
                                : "Select 2–5 screens"}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnGhost }}
                            onClick={() => {
                              setSelectedFrameIds([]);
                              setMultiSelectMode(true);
                              markActivity();
                            }}
                            disabled={analyzeBusy || frames.length < 2}
                            title={frames.length < 2 ? "At least 2 previous screens are required" : "Select multiple previous screens"}
                          >
                            Select screens
                          </button>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        ...styles.filmstripRow,
                        ...(!isDesktop ? styles.filmstripRowMobile : null),
                      }}
                    >
                      {filmstripItems.map((it) => {
                        const id = String(it.frame_id || "");
                        const isActiveTile = id === String(activeItemId || "");
                        const isMultiSelected = selectedFrameIds.includes(id);

                        if (it.kind === "live" || id === "__LIVE__") {
                          return (
                            <button
                              key={id}
                              type="button"
                              style={{
                                ...styles.thumbItem,
                                ...(!isDesktop ? styles.thumbItemMobile : null),
                                ...(isActiveTile && !multiSelectMode ? styles.thumbItemActive : null),
                                ...styles.thumbItemLive,
                                ...(multiSelectMode ? styles.thumbItemLiveDisabled : null),
                              }}
                              onClick={() => {
                                if (multiSelectMode) return;
                                setActiveFrameId(null);
                                markActivity();
                              }}
                              title={multiSelectMode ? "LIVE is not part of historical multi-select" : "Live preview"}
                              disabled={multiSelectMode}
                            >
                              LIVE
                            </button>
                          );
                        }

                        const src = getBestThumbSrc(it);

                        return (
                          <button
                            key={id}
                            type="button"
                            style={{
                              ...styles.thumbItem,
                              ...(!isDesktop ? styles.thumbItemMobile : null),
                              ...(!multiSelectMode && isActiveTile ? styles.thumbItemActive : null),
                              ...(multiSelectMode && isMultiSelected ? styles.thumbItemMultiSelected : null),
                            }}
                            onClick={() => {
                              if (multiSelectMode) {
                                toggleMultiFrameSelection(id);
                                setActiveFrameId(id);
                              } else {
                                setActiveFrameId(id);
                                requestChatFrameAnchor(id);
                              }
                              markActivity();
                            }}
                            title={multiSelectMode ? "Select or deselect this screen" : "Select screen"}
                          >
                            {src ? (
                              <img
                                src={src}
                                alt=""
                                style={styles.thumbImg}
                                draggable={false}
                                onError={() => handleThumbImgError(id)}
                              />
                            ) : (
                              <div style={styles.thumbFallback}>Frame</div>
                            )}

                            {multiSelectMode ? (
                              <span
                                style={{
                                  ...styles.thumbCheck,
                                  ...(isMultiSelected ? styles.thumbCheckSelected : null),
                                }}
                                aria-hidden="true"
                              >
                                {isMultiSelected ? "✓" : ""}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div
            role="separator"
            aria-orientation="vertical"
            title="Drag to resize"
            style={dividerStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              draggingRef.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
              markActivity();
            }}
            onTouchStart={() => {
              draggingRef.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
              markActivity();
            }}
          >
            <div style={styles.splitDividerBar} />
          </div>

          {/* CHAT */}
          <div style={chatWrapStyle} aria-hidden={!showChat}>
            <div style={styles.panel}>
              {showChatFilmstrip ? (
                renderCompactChatNavigator({ showPaneControls: isDesktop && expanded === "chat" })
              ) : (
                <div
                  style={{
                    ...styles.panelHeader,
                    ...(!isDesktop ? styles.panelHeaderMobile : null),
                  }}
                >
                  <div style={styles.panelTitle}>Vision chat</div>

                  {isDesktop ? (
                    <div style={styles.panelActions}>
                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSmall }}
                        onClick={swapPanes}
                      >
                        Swap
                      </button>

                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSmall }}
                        onClick={() => minimizePane("chat")}
                        title="Minimize this pane"
                      >
                        Minimize
                      </button>

                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnSmall }}
                        onClick={() => toggleExpand("chat")}
                        title="Expand to full view"
                      >
                        Expand
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              <div style={styles.chatWrap}>
                <VisionChatInterface {...visionChatProps} />
              </div>
            </div>
          </div>
        </div>

        {!bottomCollapsed && showMinimizedDock ? (
          <div style={styles.minDock}>
            <div style={styles.minDockLabel}>Minimized:</div>

            {collapsed.preview ? (
              <button type="button" style={{ ...styles.btn, ...styles.btnSmall }} onClick={() => restorePane("preview")}>
                Restore Preview
              </button>
            ) : null}

            {collapsed.chat ? (
              <button type="button" style={{ ...styles.btn, ...styles.btnSmall }} onClick={() => restorePane("chat")}>
                Restore Chat
              </button>
            ) : null}

            {expanded ? (
              <button type="button" style={{ ...styles.btn, ...styles.btnSmall }} onClick={() => setExpanded(null)}>
                Split view
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  return chatOnlyView ? chatOnlyRender : mainRender;
}

const styles = {
  outerWrap: {
    height: "100dvh",
    minHeight: "100dvh",
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    margin: "0 auto",
    padding: "18px 16px 24px",
    boxSizing: "border-box",
    overflow: "hidden",
    background:
      "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
  },

  outerWrapMobile: {
    padding: "8px 6px 10px",
    background: "#FFFFFF",
    height: "100dvh",
    minHeight: "100dvh",
  },

  card: {
    height: "100%",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
    padding: 16,
    background: "rgba(255,255,255,0.97)",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 20,
    boxShadow: "0 18px 42px rgba(15,23,42,0.08)",
    fontFamily: "sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative",
  },

  cardMobile: {
    maxWidth: "100%",
    margin: 0,
    padding: 8,
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
  },
  topCornerToggle: {
    position: "absolute",
    top: 12,
    right: 14,
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
    color: "#1E2A78",
    zIndex: 20,
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    flexWrap: "wrap",
    flex: "0 0 auto",
    paddingBottom: 4,
  },
  headerTitleWrap: { minWidth: 0, flex: "1 1 auto" },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },
  headerActionsMobile: {
    width: "100%",
    justifyContent: "flex-start",
  },
  headerToggleBtn: {
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#1E2A78",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.035em",
  },

  notice: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    fontSize: 13,
    flex: "0 0 auto",
  },
  noticeOk: { background: "#ecfdf5", color: "#047857", borderColor: "#a7f3d0" },
  noticeErr: { background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" },
  noticeMuted: { marginTop: 10, color: "#6b7280", fontSize: 13, flex: "0 0 auto" },

  noticeErrInline: {
    marginBottom: 10,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: 13,
  },

  // Compact header presentation for VisionAssistClockControls.
  modeRightGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },
  statusClockCol: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  statusBadgeInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    minHeight: 36,
    padding: "0 11px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#334155",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  clockPill: {
    minHeight: 36,
    minWidth: 88,
    padding: "4px 10px",
    borderRadius: 11,
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
    textAlign: "center",
    boxSizing: "border-box",
  },
  clockLabel: {
    fontSize: 9,
    color: "#64748B",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  clockValue: {
    marginTop: 1,
    fontSize: 15,
    lineHeight: 1.05,
    fontWeight: 950,
    color: "#0F172A",
  },
  clockWrapInline: { width: "auto" },

mobileTabs: {
  marginTop: 10,
  display: "flex",
  gap: 8,
},

  tabBtn: {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 800,
  color: "#111827",
  flex: 1,            // ✅ full-width tabs (each tab takes half the row)
  textAlign: "center" // ✅ nicer on mobile
},

  tabBtnActive: {
    background: "#1E2A78",
    borderColor: "#1E2A78",
    color: "#FFFFFF",
  },

  splitWrap: {
    marginTop: 14,
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    gap: 0,
    alignItems: "stretch",
    position: "relative",
  },
  splitWrapMobile: {
  // ✅ Must be flex column so the tab panel gets real height
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
},


  splitPaneWrap: {
    minHeight: 0,
    height: "100%",
  },

  splitDivider: {
    width: 14,
    cursor: "col-resize",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
  },
  splitDividerBar: {
    width: 4,
    height: "70%",
    borderRadius: 999,
    background: "#e5e7eb",
  },

  panel: {
    border: "1px solid #E2E8F0",
    borderRadius: 18,
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },

 panelHeader: {
  padding: "10px 12px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap", // ✅ avoid squeeze on mobile
},
panelHeaderMobile: {
  padding: "10px",
},
panelHeaderSub: {
  padding: "8px 12px",
  borderBottom: "1px solid #f1f5f9",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap", // ✅ avoid squeeze on mobile
},


  panelTitle: { fontSize: 14, fontWeight: 950, color: "#0F172A" },
  panelTitleSub: { fontSize: 12, fontWeight: 800, color: "#64748B" },

  panelActions: { display: "flex", gap: 8, alignItems: "center" },

  previewBody: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minHeight: 0,
    flex: "1 1 auto",
  },
  previewBodyMobile: {
    padding: 8,
    gap: 12,
  },

  videoWrapFlex: {
    position: "relative",
    width: "100%",
    flex: "1 1 auto",
    minHeight: 220,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    background: "#fff",
  },

  video: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    background: "#fff",
  },

  selectedFrameImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    background: "#fff",
    zIndex: 5,
  },

  videoOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    textAlign: "center",
    color: "#6b7280",
    fontSize: 13,
    background: "rgba(255,255,255,0.75)",
    zIndex: 6,
  },

  analyzeOverlay: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 7,
    pointerEvents: "auto",
  },

  navArrowBtn: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 8,
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    fontSize: 22,
    lineHeight: "32px",
    fontWeight: 900,
    color: "#111827",
  },
  navArrowLeft: { left: 10 },
  navArrowRight: { right: 10 },

  filmstripWrap: {
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    padding: 10,
    background: "#FFFFFF",
    minWidth: 0,
  },
  filmstripWrapMobile: {
    padding: "10px 8px",
    borderRadius: 12,
    minWidth: 0,
  },
  filmstripToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  filmstripToolbarMobile: {
    gap: 12,
    marginBottom: 10,
  },
  filmstripLabel: {
    color: "#475569",
    fontSize: 11,
    fontWeight: 900,
  },
  filmstripActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
    flexWrap: "wrap",
  },

  filmstripRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 2,
    minWidth: 0,
  },
  filmstripRowMobile: {
    gap: 12,
    padding: "2px 4px 8px",
    scrollSnapType: "x proximity",
    scrollPaddingInline: 4,
    WebkitOverflowScrolling: "touch",
  },

  // Expanded desktop Chat + mobile/history compact frame navigator.
  // This intentionally occupies the normal panel-header area instead of adding
  // a second "Screens" block above the conversation.
  chatNavigatorHeader: {
    flex: "0 0 auto",
    minWidth: 0,
    minHeight: 58,
    padding: "7px 10px",
    borderBottom: "1px solid #E2E8F0",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    boxSizing: "border-box",
  },
  chatNavigatorHeaderMobile: {
    minHeight: 0,
    padding: "9px 8px 10px",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 9,
  },
  chatNavigatorTrack: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  chatNavigatorTrackMobile: {
    width: "100%",
    gap: 8,
  },
  chatNavigatorRail: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    gap: 7,
    overflowX: "auto",
    padding: "2px 2px 3px",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
  },
  chatNavigatorRailMobile: {
    gap: 12,
    padding: "2px 4px 7px",
    scrollSnapType: "x proximity",
    scrollPaddingInline: 4,
  },
  chatNavigatorActions: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "nowrap",
  },
  chatNavigatorActionsMobile: {
    width: "100%",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  chatNavArrow: {
    flex: "0 0 30px",
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#1E2A78",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 950,
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
  },
  chatNavArrowDisabled: {
    opacity: 0.35,
    cursor: "default",
  },
  chatNavThumbDesktop: {
    width: 54,
    height: 40,
    borderRadius: 8,
  },

  thumbItem: {
    position: "relative",
    width: 62,
    height: 48,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
    cursor: "pointer",
    padding: 0,
    overflow: "hidden",
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbItemMobile: {
    width: 82,
    height: 60,
    borderRadius: 11,
    scrollSnapAlign: "start",
  },
  thumbItemActive: {
    borderColor: "#1E2A78",
    boxShadow: "0 0 0 2px rgba(30,42,120,0.18)",
  },
  thumbItemMultiSelected: {
    borderColor: "#2563EB",
    boxShadow: "0 0 0 2px rgba(37,99,235,0.24)",
  },
  thumbItemLive: {
    background: "#1E2A78",
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 900,
  },
  thumbItemLiveDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  thumbCheck: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.96)",
    background: "rgba(15,23,42,0.52)",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 950,
    boxShadow: "0 1px 4px rgba(15,23,42,0.25)",
  },
  thumbCheckSelected: {
    background: "#2563EB",
    borderColor: "#FFFFFF",
  },

  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  thumbFallback: { fontSize: 12, fontWeight: 800, color: "#6b7280" },

  chatWrap: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },

  minDock: {
    marginTop: 10,
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  minDockLabel: { fontSize: 12, fontWeight: 900, color: "#6b7280" },

  // Buttons
  btn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #1E2A78",
    background: "#1E2A78",
    color: "#FFFFFF",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1,
  },
  btnSmall: {
    padding: "8px 10px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 900,
  },
  btnGhost: {
    background: "#FFFFFF",
    color: "#1E2A78",
    borderColor: "#CBD5E1",
  },
  btnDisabled: {
    background: "#E2E8F0",
    borderColor: "#CBD5E1",
    color: "#64748B",
    cursor: "not-allowed",
  },
  btnDanger: {
    background: "#b91c1c",
    borderColor: "#b91c1c",
    color: "#fff",
  },

  // ✅ Analyze button — transparent + light blue border
  analyzeBtn: {
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid rgba(147,197,253,0.95)",
    background: "rgba(255,255,255,0.22)",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 900,
    boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
    backdropFilter: "blur(8px)",
  },
  analyzeBtnBusy: {
    opacity: 0.85,
    cursor: "not-allowed",
  },

  // Modals
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(17,24,39,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 420,
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
    padding: 14,
  },
  modalTitle: { fontSize: 16, fontWeight: 900, color: "#111827" },
  modalMsg: { marginTop: 8, fontSize: 13, color: "#374151", lineHeight: 1.35 },
  modalActions: { marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 },
};
